import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Inbox,
  BarChart2,
  Bell,
  ScrollText,
  LogOut,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Truck,
  PackageCheck,
  Radio,
  Shield,
  ShieldOff,
  ChevronDown,
  ChevronUp,
  Send,
  X,
  ChevronLeft,
  ChevronRight,
  Music,
  User,
  Globe,
  Play,
  Pause,
  Download,
  FileAudio,
  ExternalLink,
  Image as ImageIcon,
  Check,
  FileText,
  Layers,
  Tag,
  Disc,
  HardDrive,
  Mail,
  MessageSquare,
  Eye,
  Archive,
  Youtube,
  Edit3,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchAdminStats,
  fetchAllProfiles,
  updateProfileAdmin,
  updateArtistStarCount,
  updateArtistStreams,
  fetchAllSubmissions,
  updateSubmissionStatus,
  updateReleaseIsrc,
  updateTrackIsrc,
  getAdminSignedFileUrl,
  verifyAndResolveAdminFile,
  repairTrackAudioPath,
  repairReleaseCoverPath,
  downloadAdminFile,
  sendAdminNotification,
  fetchAuditLogs,
  fetchReleaseStorageIndex,
  fetchReleaseStorageHealth,
  migrateReleaseToNewStorage,
  fetchContactMessages,
  updateContactMessageStatus,
  fetchUnreadContactCount,
  fetchAdminOacRequests,
  updateOacRequestStatus,
  fetchPendingOacCount,
} from '../../lib/adminService';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import logo from '../../assets/SenritsuXwordmarkwhite.png';
import '../../styles/Admin.css';

// ── Sidebar navigation config ──────────────────────────────
const TABS = [
  { id: 'overview',      label: 'Overview',        icon: <LayoutDashboard size={17} /> },
  { id: 'artists',       label: 'Artists',          icon: <Users size={17} /> },
  { id: 'submissions',   label: 'A&R Submissions',   icon: <Inbox size={17} /> },
  { id: 'oac',           label: 'OAC Requests',     icon: <Youtube size={17} /> },
  { id: 'storage',       label: 'Storage Index',    icon: <HardDrive size={17} /> },
  { id: 'messages',      label: 'Contact Messages', icon: <Mail size={17} /> },
  { id: 'financials',    label: 'Financials',       icon: <BarChart2 size={17} /> },
  { id: 'notifications', label: 'Notifications',    icon: <Bell size={17} /> },
  { id: 'audit',         label: 'Audit Log',        icon: <ScrollText size={17} /> },
];

// ── Status config ──────────────────────────────────────────
const STATUS_CONFIG = {
  submitted:    { label: 'IN CHECK',     icon: <Clock size={11} /> },
  under_review: { label: 'In Review',    icon: <RefreshCw size={11} /> },
  approved:     { label: 'Approved',     icon: <CheckCircle size={11} /> },
  need_change:  { label: 'Need Change',  icon: <AlertTriangle size={11} /> },
  rejected:     { label: 'Rejected',     icon: <XCircle size={11} /> },
  delivery:     { label: 'Delivery',     icon: <Truck size={11} /> },
  delivered:    { label: 'Delivered',    icon: <PackageCheck size={11} /> },
  live:         { label: 'Live',         icon: <Radio size={11} /> },
  draft:        { label: 'Draft',        icon: <Clock size={11} /> },
};

// Status workflow transitions available from each state
const NEXT_STATUSES = {
  submitted:    ['under_review', 'rejected'],
  under_review: ['approved', 'need_change', 'rejected'],
  approved:     ['delivery'],
  need_change:  ['under_review', 'rejected'],
  rejected:     [],
  delivery:     ['delivered'],
  delivered:    ['live'],
  live:         [],
};

const PAGE_SIZE = 25;

// ── Helper: status badge ───────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, icon: null };
  return (
    <span className={`admin-badge ${status}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

// ── Helper: avatar initials ────────────────────────────────
function AdminAvatar({ profile, size = 32 }) {
  const [err, setErr] = useState(false);
  const initials = ((profile?.stage_name || profile?.name || profile?.email || '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2));

  if (profile?.avatar_url && !err) {
    return (
      <div className="admin-avatar" style={{ width: size, height: size }}>
        <img
          src={profile.avatar_url}
          alt={initials}
          onError={() => setErr(true)}
        />
      </div>
    );
  }
  return (
    <div className="admin-avatar" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {initials}
    </div>
  );
}

// ── Helper: skeleton rows ──────────────────────────────────
function SkeletonRows({ cols = 5, rows = 6 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <tr key={i}>
      {Array.from({ length: cols }).map((__, j) => (
        <td key={j} style={{ padding: '13px 16px' }}>
          <div className="admin-skeleton-block" style={{ width: `${50 + Math.random() * 40}%` }} />
        </td>
      ))}
    </tr>
  ));
}

// ── Modal wrapper ──────────────────────────────────────────
function Modal({ open, onClose, title, children }) {
  useEffect(() => {
    if (open) {
      const handler = (e) => { if (e.key === 'Escape') onClose(); };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="admin-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="admin-modal" role="dialog" aria-modal="true">
        <div className="admin-modal-header">
          <h2 className="admin-modal-title">{title}</h2>
          <button className="admin-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// TAB: Overview
// ══════════════════════════════════════════════════════════════
function AdminOverview() {
  const [stats, setStats] = useState({
    totalArtists: 0,
    totalReleases: 0,
    releasesThisMonth: 0,
    pendingReview: 0,
    needChange: 0,
    inQueue: 0,
    delivered: 0,
    totalStreams: 0,
    totalArtistEarnings: 0,
    newUsers: 0,
    newSubmissions: 0,
    unreadContactMessages: 0,
    pendingOacRequests: 0,
  });
  const [metricErrors, setMetricErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorNotice, setErrorNotice] = useState(null);

  const loadOverview = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setErrorNotice(null);

    try {
      const { data, errors, error } = await fetchAdminStats();
      if (data) {
        setStats(data);
      }
      setMetricErrors(errors || {});
      if (error) {
        setErrorNotice('Notice: Some statistics could not be loaded directly from Supabase.');
      }
    } catch (err) {
      console.error('[AdminOverview] Error loading stats:', err);
      setErrorNotice('Failed to reload platform statistics.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // 1. Initial load
  useEffect(() => {
    loadOverview(false);
  }, [loadOverview]);

  // 2. Realtime listener: separate lifecycle, clean unmount
  useEffect(() => {
    if (!isSupabaseConfigured()) return;

    const channelTopic = `admin_overview_stats_${Date.now()}`;
    const channel = supabase
      .channel(channelTopic)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'releases' }, () => {
        loadOverview(true);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => {
        loadOverview(true);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'distribution_submissions' }, () => {
        loadOverview(true);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'oac_requests' }, () => {
        loadOverview(true);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadOverview]);

  const cards = [
    { key: 'totalArtists', label: 'TOTAL ARTISTS', value: stats.totalArtists, colorClass: 'purple', icon: <Users size={18} /> },
    { key: 'totalReleases', label: 'TOTAL RELEASES', value: stats.totalReleases, colorClass: 'blue', icon: <Disc size={18} /> },
    { key: 'releasesThisMonth', label: 'RELEASES THIS MONTH', value: stats.releasesThisMonth, colorClass: 'blue', icon: <Music size={18} /> },
    { key: 'pendingReview', label: 'PENDING A&R', value: stats.pendingReview, colorClass: 'amber', icon: <Clock size={18} /> },
    { key: 'pendingOacRequests', label: 'PENDING OAC', value: stats.pendingOacRequests, colorClass: 'amber', icon: <Youtube size={18} /> },
    { key: 'needChange', label: 'NEED CHANGE', value: stats.needChange, colorClass: 'red', icon: <AlertTriangle size={18} /> },
    { key: 'inQueue', label: 'IN QUEUE', value: stats.inQueue, colorClass: 'amber', icon: <Truck size={18} /> },
    { key: 'delivered', label: 'DELIVERED', value: stats.delivered, colorClass: 'green', icon: <PackageCheck size={18} /> },
    { key: 'totalStreams', label: 'TOTAL STREAMS', value: stats.totalStreams, colorClass: 'green', icon: <Radio size={18} /> },
    { key: 'totalArtistEarnings', label: 'TOTAL ARTIST EARNINGS', value: stats.totalArtistEarnings, isCurrency: true, colorClass: 'green', icon: <HardDrive size={18} /> },
    { key: 'newUsers', label: 'NEW USERS', value: stats.newUsers, colorClass: 'purple', icon: <User size={18} /> },
    { key: 'newSubmissions', label: 'NEW SUBMISSIONS', value: stats.newSubmissions, colorClass: 'blue', icon: <Inbox size={18} /> },
    { key: 'unreadContactMessages', label: 'CONTACT MESSAGES', value: stats.unreadContactMessages, colorClass: 'amber', icon: <Mail size={18} /> },
  ];

  return (
    <div>
      <div className="admin-section-header">
        <div>
          <div className="admin-section-title">Platform Overview</div>
        </div>
        <button
          className="admin-btn admin-btn-ghost admin-btn-sm"
          disabled={loading || refreshing}
          onClick={() => loadOverview(true)}
          style={{ cursor: loading || refreshing ? 'not-allowed' : 'pointer', opacity: loading || refreshing ? 0.7 : 1 }}
          title="Refresh live Supabase statistics"
        >
          <RefreshCw size={13} className={refreshing ? 'admin-spin' : ''} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {errorNotice && (
        <div style={{ fontSize: '0.8rem', color: '#fbbf24', marginBottom: '1rem', background: 'rgba(251, 191, 36, 0.1)', padding: '0.4rem 0.8rem', borderRadius: '6px', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
          {errorNotice}
        </div>
      )}

      <div className="admin-stats-grid">
        {loading
          ? Array.from({ length: 13 }).map((_, i) => (
              <div key={i} className="admin-stat-card">
                <div className="admin-skeleton-block" style={{ width: 38, height: 38, borderRadius: 10 }} />
                <div className="admin-skeleton-block" style={{ width: '60%', height: 28 }} />
                <div className="admin-skeleton-block" style={{ width: '80%', height: 12 }} />
              </div>
            ))
          : cards.map((card) => {
              const hasError = !!metricErrors[card.key];
              const errorText = metricErrors[card.key];
              return (
                <div key={card.label} className="admin-stat-card">
                  <div className={`admin-stat-icon ${card.colorClass}`}>{card.icon}</div>
                  <div className="admin-stat-value">
                    {hasError ? (
                      <span
                        className="admin-stat-error"
                        title={errorText}
                        style={{
                          color: '#f87171',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '0.95rem',
                          fontWeight: 600,
                        }}
                      >
                        <AlertTriangle size={15} /> Error
                      </span>
                    ) : card.isCurrency ? (
                      `₹${(card.value ?? 0).toLocaleString()}`
                    ) : (
                      (card.value?.toLocaleString() ?? '0')
                    )}
                  </div>
                  <div className="admin-stat-label">{card.label}</div>
                </div>
              );
            })}
      </div>

      <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12, marginTop: 12 }}>
        Month counts reset on the 1st. Statistics are live from Supabase.
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// TAB: Artists
// ══════════════════════════════════════════════════════════════
function AdminArtists({ adminId }) {
  const [profiles, setProfiles] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null); // { profile, action }
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  // Manual stream management state
  const [editingStreamUserId, setEditingStreamUserId] = useState(null);
  const [streamEditValue, setStreamEditValue] = useState('');
  const [streamSaving, setStreamSaving] = useState(false);

  const handleSaveStreams = async (artistProfile) => {
    const rawVal = streamEditValue.trim();
    const count = parseInt(rawVal, 10);
    if (isNaN(count) || count < 0) {
      setAlert({ type: 'error', msg: 'Please enter a valid, non-negative stream count.' });
      return;
    }

    setStreamSaving(true);
    const { success, streams, error } = await updateArtistStreams(artistProfile.id, count, adminId);
    setStreamSaving(false);

    if (error || !success) {
      setAlert({ type: 'error', msg: 'Failed to update streams: ' + (error?.message || error || 'Unknown error') });
    } else {
      const artistName = artistProfile.stage_name || artistProfile.name || 'Artist';
      setAlert({ type: 'success', msg: `Successfully updated streams for ${artistName} to ${streams.toLocaleString()}.` });
      setEditingStreamUserId(null);
      setStreamEditValue('');
      load(page, search);
    }
  };

  // Star assignment state & handlers
  const [updatingStarUserId, setUpdatingStarUserId] = useState(null);

  const handleUpdateStars = async (profileItem, newCount) => {
    const validCount = Math.max(0, Math.min(5, Number(newCount) || 0));
    const previousCount = profileItem.star_count || 0;
    if (validCount === previousCount) return;

    setUpdatingStarUserId(profileItem.id);
    setAlert(null);

    // Optimistic update
    setProfiles((prev) =>
      prev.map((p) => (p.id === profileItem.id ? { ...p, star_count: validCount } : p))
    );

    try {
      const res = await updateArtistStarCount(profileItem.id, validCount, adminId);
      if (!res?.success) {
        throw new Error(res?.error?.message || res?.error || 'Failed to update star rating.');
      }
      setAlert({
        type: 'success',
        msg: `Updated stars for ${profileItem.stage_name || profileItem.name || 'artist'} to ${validCount}★.`,
      });
      load(page, search);
    } catch (err) {
      console.error('[AdminArtists] Star update error:', err);
      // Revert optimistic update
      setProfiles((prev) =>
        prev.map((p) => (p.id === profileItem.id ? { ...p, star_count: previousCount } : p))
      );
      setAlert({
        type: 'error',
        msg: `Star update failed: ${err.message || 'Unauthorized or server error.'}`,
      });
    } finally {
      setUpdatingStarUserId(null);
    }
  };

  const load = useCallback(
    (pg = page, q = search) => {
      setLoading(true);
      fetchAllProfiles(q, pg).then(({ data, count: c }) => {
        setProfiles(data);
        setCount(c);
        setLoading(false);
      });
    },
    [page, search]
  );

  useEffect(() => { load(1, search); }, []);// eslint-disable-line

  const doSearch = () => { setPage(1); load(1, search); };

  const openAction = (profile, action) => {
    setActionModal({ profile, action });
    setActionReason('');
    setAlert(null);
  };

  const closeModal = () => { setActionModal(null); setActionReason(''); };

  const commitAction = async () => {
    if (!actionModal) return;
    const { profile, action } = actionModal;
    setActionLoading(true);

    let updates = {};
    if (action === 'verify')    updates = { is_verified: true };
    if (action === 'unverify')  updates = { is_verified: false };
    if (action === 'suspend')   updates = { is_suspended: true };
    if (action === 'unsuspend') updates = { is_suspended: false };

    const oldValues = {
      is_verified: profile.is_verified,
      is_suspended: profile.is_suspended,
    };

    const { error } = await updateProfileAdmin(profile.id, updates, actionReason, adminId, oldValues);
    setActionLoading(false);

    if (error) {
      setAlert({ type: 'error', msg: 'Action failed: ' + (error.message || error) });
    } else {
      setAlert({ type: 'success', msg: 'Profile updated successfully.' });
      closeModal();
      load(page, search);
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div>
      <div className="admin-section-header">
        <div className="admin-section-title">Artists &amp; Profiles</div>
        <div className="admin-filter-bar">
          <input
            id="admin-artist-search"
            className="admin-search-input"
            placeholder="Search name, stage name, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doSearch()}
          />
          <button className="admin-btn admin-btn-ghost admin-btn-sm" onClick={doSearch}>
            <Search size={13} /> Search
          </button>
        </div>
      </div>

      {alert && (
        <div className={`admin-alert admin-alert-${alert.type}`}>
          {alert.msg}
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Full Name</th>
              <th>Stage Name</th>
              <th>Email</th>
              <th>Profession / Role</th>
              <th>Genre</th>
              <th>City</th>
              <th>Followers</th>
              <th>Streams</th>
              <th>Verification</th>
              <th>Artist Web Stars</th>
              <th>Created Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows cols={12} rows={8} />
            ) : profiles.length === 0 ? (
              <tr>
                <td colSpan={12}>
                  <div className="admin-empty">
                    <User size={32} className="admin-empty-icon" />
                    <div className="admin-empty-title">No artists found</div>
                    <div className="admin-empty-sub">Try a different search term.</div>
                  </div>
                </td>
              </tr>
            ) : (
              profiles.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="admin-user-cell">
                      <AdminAvatar profile={p} size={30} />
                      <div style={{ fontWeight: 600, color: '#fff', fontSize: '13px' }}>
                        {p.name || 'Not provided'}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span style={{ color: '#d9fb06', fontWeight: 600, fontSize: '13px' }}>
                      {p.stage_name || 'Not provided'}
                    </span>
                  </td>
                  <td style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                    {p.email || 'Not provided'}
                  </td>
                  <td style={{ color: '#fff', fontSize: 12 }}>
                    {p.profession || (p.creator_roles?.length ? p.creator_roles.join(', ') : p.role) || 'Not provided'}
                  </td>
                  <td style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                    {p.genre || 'Not provided'}
                  </td>
                  <td style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                    {p.city || 'Not provided'}
                  </td>
                  <td>
                    <span style={{ fontWeight: 700, color: '#d9fb06', fontSize: 13 }}>
                      {p.follower_count !== undefined ? p.follower_count : 0}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {editingStreamUserId === p.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap' }}>
                        <input
                          type="number"
                          min="0"
                          className="admin-search-input"
                          style={{
                            width: '88px',
                            padding: '3px 6px',
                            fontSize: '12px',
                            height: '26px',
                            background: 'rgba(0,0,0,0.6)',
                            border: '1px solid #d9fb06',
                            color: '#fff',
                            borderRadius: '4px',
                          }}
                          value={streamEditValue}
                          onChange={(e) => setStreamEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveStreams(p);
                            if (e.key === 'Escape') {
                              setEditingStreamUserId(null);
                              setStreamEditValue('');
                            }
                          }}
                          disabled={streamSaving}
                          autoFocus
                        />
                        <button
                          type="button"
                          className="admin-btn admin-btn-primary admin-btn-xs"
                          style={{ padding: '2px 8px', fontSize: '11px', height: '26px', fontWeight: 600 }}
                          disabled={streamSaving}
                          onClick={() => handleSaveStreams(p)}
                        >
                          {streamSaving ? '...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-ghost admin-btn-xs"
                          style={{ padding: '2px 6px', fontSize: '11px', height: '26px' }}
                          disabled={streamSaving}
                          onClick={() => {
                            setEditingStreamUserId(null);
                            setStreamEditValue('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontWeight: 600, color: '#fff', fontSize: '13px' }}>
                          {(p.total_streams !== undefined && p.total_streams !== null ? Number(p.total_streams) : 0).toLocaleString()}
                        </span>
                        <button
                          type="button"
                          className="admin-btn admin-btn-ghost admin-btn-xs"
                          style={{
                            padding: '2px 6px',
                            fontSize: '11px',
                            height: '22px',
                            border: '1px solid rgba(255,255,255,0.18)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            setEditingStreamUserId(p.id);
                            setStreamEditValue(p.total_streams !== undefined && p.total_streams !== null ? String(p.total_streams) : '0');
                          }}
                          title={`Edit streams for ${p.stage_name || p.name || 'artist'}`}
                        >
                          <Edit3 size={10} style={{ marginRight: '2px' }} /> Edit
                        </button>
                      </div>
                    )}
                  </td>
                  <td>
                    {p.is_verified ? (
                      <span className="admin-badge verified"><CheckCircle size={10} /> Verified</span>
                    ) : (
                      <span className="admin-badge" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>Standard</span>
                    )}
                    {p.is_suspended && (
                      <span className="admin-badge suspended" style={{ marginLeft: 4 }}><XCircle size={10} /> Suspended</span>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <button
                        type="button"
                        disabled={updatingStarUserId === p.id || (p.star_count || 0) <= 0}
                        onClick={() => handleUpdateStars(p, (p.star_count || 0) - 1)}
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          color: '#fff',
                          borderRadius: '4px',
                          width: '22px',
                          height: '22px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: (updatingStarUserId === p.id || (p.star_count || 0) <= 0) ? 'not-allowed' : 'pointer',
                          opacity: (updatingStarUserId === p.id || (p.star_count || 0) <= 0) ? 0.35 : 1,
                          fontSize: '12px',
                          fontWeight: 'bold',
                        }}
                        title="Decrease stars"
                      >
                        -
                      </button>
                      <span style={{ color: '#d9fb06', fontSize: '0.85rem', fontWeight: 700, minWidth: '40px', textAlign: 'center', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                        {updatingStarUserId === p.id ? (
                          <RefreshCw size={11} className="admin-spin" style={{ color: '#d9fb06' }} />
                        ) : (p.star_count || 0) > 0 ? (
                          '★'.repeat(p.star_count)
                        ) : (
                          '0'
                        )}
                      </span>
                      <button
                        type="button"
                        disabled={updatingStarUserId === p.id || (p.star_count || 0) >= 5}
                        onClick={() => handleUpdateStars(p, (p.star_count || 0) + 1)}
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          color: '#fff',
                          borderRadius: '4px',
                          width: '22px',
                          height: '22px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: (updatingStarUserId === p.id || (p.star_count || 0) >= 5) ? 'not-allowed' : 'pointer',
                          opacity: (updatingStarUserId === p.id || (p.star_count || 0) >= 5) ? 0.35 : 1,
                          fontSize: '12px',
                          fontWeight: 'bold',
                        }}
                        title="Increase stars"
                      >
                        +
                      </button>
                      <select
                        value={p.star_count || 0}
                        disabled={updatingStarUserId === p.id}
                        onChange={(e) => handleUpdateStars(p, Number(e.target.value))}
                        style={{
                          background: 'rgba(0,0,0,0.4)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          color: '#fff',
                          borderRadius: '4px',
                          padding: '0.15rem 0.3rem',
                          fontSize: '0.72rem',
                          cursor: updatingStarUserId === p.id ? 'not-allowed' : 'pointer',
                          opacity: updatingStarUserId === p.id ? 0.6 : 1,
                          marginLeft: '0.2rem',
                        }}
                        title="ARTIST WEB STARS (0-5 stars)"
                      >
                        <option value={0}>0 Stars</option>
                        <option value={1}>1 Star (★)</option>
                        <option value={2}>2 Stars (★★)</option>
                        <option value={3}>3 Stars (★★★)</option>
                        <option value={4}>4 Stars (★★★★)</option>
                        <option value={5}>5 Stars (★★★★★)</option>
                      </select>
                    </div>
                  </td>
                  <td style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Not provided'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {p.is_verified ? (
                        <button className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => openAction(p, 'unverify')}>
                          <ShieldOff size={12} /> Unverify
                        </button>
                      ) : (
                        <button className="admin-btn admin-btn-success admin-btn-sm" onClick={() => openAction(p, 'verify')}>
                          <Shield size={12} /> Verify
                        </button>
                      )}
                      {p.is_suspended ? (
                        <button className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => openAction(p, 'unsuspend')}>
                          <CheckCircle size={12} /> Unsuspend
                        </button>
                      ) : (
                        <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => openAction(p, 'suspend')}>
                          <XCircle size={12} /> Suspend
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="admin-pagination">
          <span>{count} total</span>
          <div className="admin-pagination-controls">
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={page <= 1}
              onClick={() => { setPage(page - 1); load(page - 1, search); }}
            >
              <ChevronLeft size={14} />
            </button>
            <span style={{ fontSize: 13 }}>Page {page} / {totalPages}</span>
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={page >= totalPages}
              onClick={() => { setPage(page + 1); load(page + 1, search); }}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Action Modal */}
      <Modal
        open={!!actionModal}
        onClose={closeModal}
        title={
          actionModal?.action === 'verify'    ? 'Verify Artist' :
          actionModal?.action === 'unverify'  ? 'Remove Verification' :
          actionModal?.action === 'suspend'   ? 'Suspend Account' :
          actionModal?.action === 'unsuspend' ? 'Unsuspend Account' : 'Confirm Action'
        }
      >
        {actionModal && (
          <>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, marginBottom: 18 }}>
              Target: <strong style={{ color: '#e8e8f0' }}>{actionModal.profile.stage_name || actionModal.profile.name}</strong>
            </p>
            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="admin-action-reason">
                Reason <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional but recommended)</span>
              </label>
              <textarea
                id="admin-action-reason"
                className="admin-form-input admin-form-textarea"
                placeholder="Document the reason for this action…"
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
              />
            </div>
            {alert && <div className={`admin-alert admin-alert-${alert.type}`}>{alert.msg}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="admin-btn admin-btn-ghost" onClick={closeModal} disabled={actionLoading}>
                Cancel
              </button>
              <button
                className={`admin-btn ${
                  actionModal.action === 'suspend' ? 'admin-btn-danger' :
                  actionModal.action === 'verify'  ? 'admin-btn-success' :
                  'admin-btn-ghost'
                }`}
                onClick={commitAction}
                disabled={actionLoading}
              >
                {actionLoading ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// COMPONENT: Comprehensive A&R Submission Detail View
// ══════════════════════════════════════════════════════════════
function AdminSubmissionDetail({ sub, adminId, onRefresh }) {
  const release = sub.releases || {};
  const artist = sub.profiles || {};
  const tracks = release.release_tracks || [];
  const monetisation = release.release_social_monetisation || {};
  const firstTrack = tracks[0] || {};

  // Cover Art state
  const [coverState, setCoverState] = useState({
    loading: true,
    url: null,
    path: null,
    exists: false,
    message: null,
  });
  const [coverDownloading, setCoverDownloading] = useState(false);
  const [zoomCover, setZoomCover] = useState(false);

  // Track Audio & File states: { [trackId]: { loading: boolean, url: string|null, path: string|null, exists: boolean, message: string|null, errorType: string|null } }
  const [trackFileStates, setTrackFileStates] = useState({});
  const [downloadingTrack, setDownloadingTrack] = useState({});

  // Audio Player state
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  // ISRC & alerts
  const [releaseIsrcVal, setReleaseIsrcVal] = useState(release.isrc || '');
  const [releaseIsrcSaving, setReleaseIsrcSaving] = useState(false);
  const [trackIsrcVals, setTrackIsrcVals] = useState({});
  const [trackIsrcSaving, setTrackIsrcSaving] = useState({});
  const [detailAlert, setDetailAlert] = useState(null);
  const [expandedLyrics, setExpandedLyrics] = useState({});

  // 1. Authoritative File Verification & Diagnostic on Mount (Section 15 & Section 24)
  useEffect(() => {
    let isMounted = true;

    // (a) Verify Cover Art
    const verifyCover = async () => {
      setCoverState((prev) => ({ ...prev, loading: true }));

      const regNo = release.senritsux_registration_number || sub.reference_code;
      const candidateFolders = [];
      if (regNo) {
        candidateFolders.push(regNo);
      }
      if (sub.user_id && sub.id) {
        candidateFolders.push(`${sub.user_id}/${sub.id}/cover`);
      }
      if (sub.user_id && release.id && release.id !== sub.id) {
        candidateFolders.push(`${sub.user_id}/${release.id}/cover`);
      }
      if (sub.user_id && sub.reference_code) {
        candidateFolders.push(`${sub.user_id}/${sub.reference_code}/cover`);
      }
      if (sub.user_id && release.senritsux_registration_number) {
        candidateFolders.push(`${sub.user_id}/${release.senritsux_registration_number}/cover`);
      }

      const coverRes = await verifyAndResolveAdminFile({
        bucket: release.cover_bucket || 'covers',
        candidatePaths: [
          release.cover_storage_path,
          regNo ? `${regNo}/cover.jpg` : null,
          release.cover_processed_path,
          release.cover_url,
        ].filter(Boolean),
        candidateFolders,
        folderType: 'cover',
        debugContext: {
          submission_id: sub.id,
          user_id: sub.user_id,
          track_id: 'cover',
          senritsux_registration_number: regNo,
        },
        autoRepairCallback: async (discoveredPath) => {
          await repairReleaseCoverPath(release.id, discoveredPath, adminId);
        },
      });

      if (isMounted) {
        setCoverState({
          loading: false,
          url: coverRes.url,
          path: coverRes.path,
          exists: coverRes.exists,
          message: coverRes.message || null,
        });
      }
    };

    // (b) Verify Every Audio Track
    const verifyTracks = async () => {
      const initialMap = {};
      tracks.forEach((t) => {
        initialMap[t.id] = { loading: true, url: null, path: null, exists: false, message: null };
      });
      setTrackFileStates(initialMap);

      const regNo = release.senritsux_registration_number || sub.reference_code;
      const verifiedMap = {};
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];

        // Requirement 1: Log raw DB values before verification
        console.log('[AdminPage verifyTracks] Track ID:', track.id, {
          raw_audio_processed_path: JSON.stringify(track.audio_processed_path),
          raw_audio_original_path: JSON.stringify(track.audio_original_path),
          raw_storage_path: JSON.stringify(track.storage_path),
          title: track.title,
        });

        // Canonical and candidate folder prefixes for tracks
        const candidateFolders = [];
        if (regNo) {
          candidateFolders.push(regNo);
        }
        if (sub.user_id && sub.id) {
          candidateFolders.push(`${sub.user_id}/${sub.id}/tracks`);
        }
        if (sub.user_id && release.id && release.id !== sub.id) {
          candidateFolders.push(`${sub.user_id}/${release.id}/tracks`);
        }
        if (sub.user_id && sub.reference_code) {
          candidateFolders.push(`${sub.user_id}/${sub.reference_code}/tracks`);
        }
        if (sub.user_id && release.senritsux_registration_number) {
          candidateFolders.push(`${sub.user_id}/${release.senritsux_registration_number}/tracks`);
        }

        const trackRes = await verifyAndResolveAdminFile({
          bucket: track.storage_bucket || 'tracks',
          candidatePaths: [
            track.storage_path,
            track.audio_processed_path,
            track.audio_original_path,
          ].filter(Boolean),
          candidateFolders,
          folderType: 'tracks',
          trackInfo: {
            trackNumber: track.track_number || (i + 1),
            trackIndex: i,
            totalTracks: tracks.length,
            originalFilename: track.audio_original_path,
            title: track.title,
            registrationNumber: regNo,
          },
          debugContext: {
            submission_id: sub.id,
            user_id: sub.user_id,
            track_id: track.id,
            senritsux_registration_number: regNo,
          },
          autoRepairCallback: async (discoveredPath) => {
            await repairTrackAudioPath(track.id, discoveredPath, adminId, track.audio_original_path);
          },
        });

        verifiedMap[track.id] = {
          loading: false,
          url: trackRes.url,
          path: trackRes.path,
          exists: trackRes.exists,
          message: trackRes.message || null,
          errorType: trackRes.errorType || null,
        };
      }

      if (isMounted) {
        setTrackFileStates(verifiedMap);
      }
    };

    verifyCover();
    verifyTracks();

    return () => {
      isMounted = false;
    };
  }, [sub.id, release.id]); // eslint-disable-line

  // Format seconds to mm:ss
  const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  // Play / pause a track (Section 4)
  const togglePlayTrack = (track) => {
    const fileState = trackFileStates[track.id];

    if (!fileState || !fileState.exists || !fileState.url) {
      const msg = fileState?.message || 'Audio file is unavailable in Storage.';
      setDetailAlert({ type: 'error', msg: `Track #${track.track_number}: ${msg}` });
      return;
    }

    if (playingTrackId === track.id) {
      if (audioRef.current) {
        if (audioRef.current.paused) {
          audioRef.current.play();
        } else {
          audioRef.current.pause();
          setPlayingTrackId(null);
        }
      }
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = fileState.url;
      audioRef.current.load();
      audioRef.current.play().catch((e) => {
        console.warn('Audio play failed:', e);
        setDetailAlert({ type: 'error', msg: 'Audio playback failed in browser.' });
      });
      setPlayingTrackId(track.id);
    }
  };

  // Download real master WAV file (Section 5, 14: clean filename e.g. 01_Banzara.wav)
  const handleDownloadTrack = async (track) => {
    const fileState = trackFileStates[track.id];
    if (!fileState || !fileState.exists || !fileState.url) {
      const msg = fileState?.message || 'Audio file is unavailable in Storage.';
      setDetailAlert({ type: 'error', msg: `Download failed: ${msg}` });
      return;
    }

    setDownloadingTrack((prev) => ({ ...prev, [track.id]: true }));
    const numStr = String(track.track_number || 1).padStart(2, '0');
    const safeTitle = (track.title || 'track').replace(/[^a-zA-Z0-9._-]/g, '_');
    const filename = `${numStr}_${safeTitle}.wav`;

    await downloadAdminFile(fileState.url, filename);
    setDownloadingTrack((prev) => ({ ...prev, [track.id]: false }));
  };

  // Download real Cover Artwork (Section 6, 15: cover.jpg)
  const handleDownloadCover = async () => {
    if (!coverState.exists || !coverState.url) {
      setDetailAlert({ type: 'error', msg: coverState.message || 'Cover artwork is unavailable in Storage.' });
      return;
    }

    setCoverDownloading(true);
    const filename = 'cover.jpg';
    await downloadAdminFile(coverState.url, filename);
    setCoverDownloading(false);
  };

  // 1-Click Non-destructive Migration for Legacy Releases
  const [migratingStorage, setMigratingStorage] = useState(false);
  const handleMigrateStorage = async () => {
    if (!release.id) return;
    setMigratingStorage(true);
    const res = await migrateReleaseToNewStorage(release.id, adminId);
    setMigratingStorage(false);
    if (res.success) {
      setDetailAlert({ type: 'success', msg: `Storage successfully synced to 'tracks' and 'covers' buckets for ${res.registrationNumber}.` });
      if (onRefresh) onRefresh();
      verifyCover();
      verifyTracks();
    } else {
      setDetailAlert({ type: 'error', msg: `Storage sync notice: ${res.error || 'Check files'}` });
    }
  };

  // View / Download Supporting Document (Section 7)
  const handleViewDoc = async (path) => {
    const res = await getAdminSignedFileUrl('audio', path, 3600);
    if (res.url) {
      window.open(res.url, '_blank', 'noopener,noreferrer');
    } else {
      setDetailAlert({ type: 'error', msg: 'Supporting document is unavailable in Storage.' });
    }
  };

  const handleDownloadDoc = async (path, filename) => {
    const res = await getAdminSignedFileUrl('audio', path, 3600);
    if (res.url) {
      await downloadAdminFile(res.url, filename);
    } else {
      setDetailAlert({ type: 'error', msg: 'Unable to download document: file not found in Storage.' });
    }
  };

  // ISRC updates
  const handleSaveReleaseIsrc = async () => {
    const val = (releaseIsrcVal || '').trim();
    if (!val || !release.id) return;
    setReleaseIsrcSaving(true);
    const { error } = await updateReleaseIsrc(release.id, val, adminId);
    setReleaseIsrcSaving(false);
    if (error) {
      setDetailAlert({ type: 'error', msg: 'Failed to update Release ISRC: ' + (error.message || error) });
    } else {
      setDetailAlert({ type: 'success', msg: `Release ISRC saved: ${val}` });
      if (onRefresh) onRefresh();
    }
  };

  const handleSaveTrackIsrc = async (trackId) => {
    const val = (trackIsrcVals[trackId] ?? '').trim();
    if (!val) return;
    setTrackIsrcSaving((prev) => ({ ...prev, [trackId]: true }));
    const { error } = await updateTrackIsrc(trackId, val, adminId);
    setTrackIsrcSaving((prev) => ({ ...prev, [trackId]: false }));
    if (error) {
      setDetailAlert({ type: 'error', msg: 'Failed to update Track ISRC: ' + (error.message || error) });
    } else {
      setDetailAlert({ type: 'success', msg: `Track ISRC saved: ${val}` });
      if (onRefresh) onRefresh();
    }
  };

  // Helper for displaying values exactly or "Not provided" (Section 13)
  const displayVal = (v) => {
    if (v === null || v === undefined || v === '') return 'Not provided';
    return v;
  };

  const primaryArtistsStr = Array.isArray(release.primary_artists) && release.primary_artists.length > 0
    ? release.primary_artists.join(', ')
    : displayVal(release.primary_artists || artist.stage_name || artist.name);

  const featuringArtistsStr = Array.isArray(release.featuring_artists) && release.featuring_artists.length > 0
    ? release.featuring_artists.join(', ')
    : displayVal(release.featuring_artists);

  const uploadedAudioCount = tracks.filter((t) => trackFileStates[t.id]?.exists).length;

  return (
    <div className="admin-sub-detail-wrapper">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onTimeUpdate={() => {
          if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
            setDuration(audioRef.current.duration || 0);
          }
        }}
        onEnded={() => {
          setPlayingTrackId(null);
          setCurrentTime(0);
        }}
      />

      {detailAlert && (
        <div className={`admin-alert admin-alert-${detailAlert.type}`}>
          {detailAlert.msg}
          <button
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
            onClick={() => setDetailAlert(null)}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Section 10: RELEASE & STORAGE ARCHITECTURE STATUS */}
      <div className="admin-storage-summary-card">
        <div className="admin-storage-summary-header">
          <div className="admin-storage-summary-title">
            <HardDrive size={16} />
            <span>RELEASE &amp; STORAGE FILES</span>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#a78bfa', fontWeight: 700 }}>
              {release.senritsux_registration_number || sub.reference_code || 'SX-PENDING'}
            </span>
            <button
              type="button"
              className="admin-btn admin-btn-ghost admin-btn-sm"
              onClick={handleMigrateStorage}
              disabled={migratingStorage}
              title="Sync or migrate legacy files to dedicated tracks and covers buckets without deleting originals"
            >
              <RefreshCw size={12} className={migratingStorage ? 'spin' : ''} />
              <span>{migratingStorage ? 'Syncing…' : 'Sync / Migrate Storage'}</span>
            </button>
          </div>
        </div>

        <div className="admin-storage-summary-grid">
          <div className="admin-storage-item">
            <span className="admin-storage-label">Release Title</span>
            <span className="admin-storage-val" style={{ fontWeight: 600 }}>{release.title || 'Untitled'}</span>
          </div>
          <div className="admin-storage-item">
            <span className="admin-storage-label">Artist</span>
            <span className="admin-storage-val">{artist.stage_name || artist.name || 'Unknown Artist'}</span>
          </div>
          <div className="admin-storage-item">
            <span className="admin-storage-label">Release Type</span>
            <span className="admin-storage-val" style={{ textTransform: 'capitalize' }}>{release.release_type || 'Single'}</span>
          </div>
          <div className="admin-storage-item">
            <span className="admin-storage-label">Audio Files</span>
            <span className="admin-storage-val">
              <span className={`admin-health-badge ${uploadedAudioCount >= tracks.length && tracks.length > 0 ? 'ok' : 'warn'}`}>
                {uploadedAudioCount} / {tracks.length} {uploadedAudioCount >= tracks.length && tracks.length > 0 ? 'uploaded ✓' : 'missing ⚠'}
              </span>
            </span>
          </div>
          <div className="admin-storage-item">
            <span className="admin-storage-label">Cover Art</span>
            <span className="admin-storage-val">
              <span className={`admin-health-badge ${coverState.exists ? 'ok' : 'warn'}`}>
                {coverState.exists ? '1 / 1 uploaded ✓' : '0 / 1 missing ⚠'}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Top Header Card: Artwork + Title + Actions */}
      <div className="admin-sub-header-bar">
        {/* Cover Art Box with Preview & Download Actions */}
        <div className="admin-cover-art-container">
          <div
            className="admin-sub-cover-wrap"
            onClick={() => coverState.exists && setZoomCover(true)}
            title={coverState.exists ? 'Click to zoom high-resolution cover artwork' : 'Cover artwork'}
          >
            {coverState.loading ? (
              <div className="admin-cover-loading-text">Loading artwork…</div>
            ) : coverState.exists && coverState.url ? (
              <>
                <img src={coverState.url} alt={release.title} />
                <div className="admin-sub-cover-overlay">
                  <ImageIcon size={18} />
                  <span>3000px</span>
                </div>
              </>
            ) : (
              <div className="admin-cover-missing-box">
                <Disc size={28} />
                <span>{coverState.message || 'Cover unavailable'}</span>
              </div>
            )}
          </div>

          <div className="admin-cover-actions">
            <button
              type="button"
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={!coverState.exists || !coverState.url}
              onClick={() => setZoomCover(true)}
              title="Preview 3000x3000 high-res cover art"
            >
              <ImageIcon size={13} />
              <span>Preview Cover</span>
            </button>
            <button
              type="button"
              className="admin-btn admin-btn-primary admin-btn-sm"
              disabled={!coverState.exists || !coverState.url || coverDownloading}
              onClick={handleDownloadCover}
              title="Download real master cover art (3000x3000px)"
            >
              <Download size={13} />
              <span>{coverDownloading ? 'Downloading…' : 'Download Cover Art'}</span>
            </button>
          </div>
        </div>

        {/* Title and Badge Info */}
        <div className="admin-sub-title-col">
          <div className="admin-sub-reg-badge">
            <Tag size={13} />
            <span>{release.senritsux_registration_number || sub.reference_code || 'SX-PENDING'}</span>
          </div>
          <h3 className="admin-sub-title">{release.title || 'Untitled Release'}</h3>
          <div className="admin-sub-artist-line">
            by <strong>{artist.stage_name || artist.name || 'Unknown Artist'}</strong>
            {artist.legal_name && (
              <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: 8 }}>
                (Legal: {artist.legal_name})
              </span>
            )}
            <span style={{ margin: '0 8px', color: 'rgba(255,255,255,0.2)' }}>•</span>
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>{artist.email || 'No email'}</span>
          </div>

          <div className="admin-sub-chips-row">
            <span className="admin-sub-chip highlight">{(release.release_type || 'SINGLE').toUpperCase()}</span>
            <span className="admin-sub-chip">{release.genre || 'Genre N/A'}</span>
            {release.mood && <span className="admin-sub-chip">{release.mood}</span>}
            <span className="admin-sub-chip">Tier: {release.price_tier || 'White'}</span>
            <span className="admin-sub-chip">{tracks.length} {tracks.length === 1 ? 'Track' : 'Tracks'}</span>
          </div>
        </div>

        {/* Release ISRC Input Box */}
        <div style={{ minWidth: 240, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Release ISRC
          </div>
          <div className="admin-isrc-input-wrap">
            <input
              className="admin-isrc-input"
              placeholder="e.g. INXYZ2600001"
              value={releaseIsrcVal}
              onChange={(e) => setReleaseIsrcVal(e.target.value)}
            />
            <button
              type="button"
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={releaseIsrcSaving}
              onClick={handleSaveReleaseIsrc}
            >
              {releaseIsrcSaving ? '…' : 'Save'}
            </button>
          </div>
          {release.isrc && (
            <span style={{ fontSize: 11, color: '#22c55e', fontFamily: 'monospace' }}>
              ✓ Current: {release.isrc}
            </span>
          )}
        </div>
      </div>

      {/* ── 7 LOGICAL METADATA SECTIONS (PRESERVING ARTIST FORM STRUCTURE) ── */}

      {/* SECTION 1: RELEASE INFORMATION */}
      <div className="admin-meta-section">
        <div className="admin-meta-section-title">
          <Disc size={15} />
          <span>Release Information</span>
        </div>
        <div className="admin-meta-grid">
          <div className="admin-meta-item">
            <span className="admin-meta-label">Release Title</span>
            <span className="admin-meta-val">{displayVal(release.title)}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Release Type</span>
            <span className="admin-meta-val">{release.release_type ? release.release_type.toUpperCase() : 'Not provided'}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Primary Genre</span>
            <span className="admin-meta-val">{displayVal(release.genre)}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Release Mood</span>
            <span className="admin-meta-val">{displayVal(release.mood)}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Language</span>
            <span className="admin-meta-val">{displayVal(firstTrack.language)}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Country Recorded In</span>
            <span className="admin-meta-val">{displayVal(firstTrack.country_recorded)}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Recording Year</span>
            <span className="admin-meta-val">{firstTrack.recording_year ? String(firstTrack.recording_year) : 'Not provided'}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Price Tier</span>
            <span className="admin-meta-val">{displayVal(release.price_tier || 'White')}</span>
          </div>
        </div>
      </div>

      {/* SECTION 2: RIGHTS INFORMATION */}
      <div className="admin-meta-section">
        <div className="admin-meta-section-title">
          <Shield size={15} />
          <span>Rights Information</span>
        </div>
        <div className="admin-meta-grid">
          <div className="admin-meta-item" style={{ gridColumn: 'span 2' }}>
            <span className="admin-meta-label">P Line (Sound Recording Copyright)</span>
            <span className="admin-meta-val">{displayVal(release.p_line)}</span>
          </div>
          <div className="admin-meta-item" style={{ gridColumn: 'span 2' }}>
            <span className="admin-meta-label">C Line (Underlying Works Copyright)</span>
            <span className="admin-meta-val">{displayVal(release.c_line)}</span>
          </div>
        </div>
      </div>

      {/* SECTION 3: ARTIST INFORMATION */}
      <div className="admin-meta-section">
        <div className="admin-meta-section-title">
          <Users size={15} />
          <span>Artist Information</span>
        </div>
        <div className="admin-meta-grid">
          <div className="admin-meta-item">
            <span className="admin-meta-label">Primary Artist(s)</span>
            <span className="admin-meta-val">{primaryArtistsStr}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Featuring Artist(s)</span>
            <span className="admin-meta-val">{featuringArtistsStr}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Artist Legal Name</span>
            <span className="admin-meta-val">{displayVal(artist.legal_name)}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Artist Account Email</span>
            <span className="admin-meta-val">{displayVal(artist.email)}</span>
          </div>
        </div>
      </div>

      {/* SECTION 4: RELEASE DATES */}
      <div className="admin-meta-section">
        <div className="admin-meta-section-title">
          <Clock size={15} />
          <span>Release Dates</span>
        </div>
        <div className="admin-meta-grid">
          <div className="admin-meta-item">
            <span className="admin-meta-label">Release Date</span>
            <span className="admin-meta-val">{displayVal(release.release_date)}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Consumer Release Date</span>
            <span className="admin-meta-val">{displayVal(release.consumer_release_date || release.release_date)}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Original Release Date</span>
            <span className="admin-meta-val">{displayVal(release.original_release_date)}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Release Time &amp; Zone</span>
            <span className="admin-meta-val">{release.release_time || '00:00:00'} ({release.release_timezone || 'Asia/Kolkata'})</span>
          </div>
        </div>
      </div>

      {/* SECTION 5: SOCIAL MONETISATION */}
      <div className="admin-meta-section">
        <div className="admin-meta-section-title">
          <Radio size={15} />
          <span>Social Monetisation</span>
        </div>
        <div className="admin-meta-grid">
          <div className="admin-meta-item">
            <span className="admin-meta-label">Instagram Music</span>
            <span className="admin-meta-val">{monetisation.instagram_music !== false ? 'Yes' : 'No'}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">TikTok</span>
            <span className="admin-meta-val">{monetisation.tiktok !== false ? 'Yes' : 'No'}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">YouTube</span>
            <span className="admin-meta-val">{monetisation.youtube !== false ? 'Yes' : 'No'}</span>
          </div>
          <div className="admin-meta-item">
            <span className="admin-meta-label">Submitted At</span>
            <span className="admin-meta-val">{sub.submitted_at ? new Date(sub.submitted_at).toLocaleString('en-IN') : 'Not provided'}</span>
          </div>
        </div>
      </div>

      {/* SECTION 6: TRACK INFORMATION & CREDITS (MULTI-TRACK SUPPORT) */}
      <div className="admin-meta-section">
        <div className="admin-meta-section-title">
          <Layers size={15} />
          <span>Track Information &amp; Audio Inspection ({tracks.length})</span>
        </div>

        {tracks.length === 0 ? (
          <div style={{ padding: 14, color: 'rgba(255,255,255,0.4)', fontSize: 13, background: 'rgba(255,255,255,0.02)', borderRadius: 8 }}>
            No tracks found for this release record.
          </div>
        ) : (
          [...tracks]
            .sort((a, b) => a.track_number - b.track_number)
            .map((track) => {
              const fileState = trackFileStates[track.id] || { loading: true };
              const isPlaying = playingTrackId === track.id;
              const hasLyrics = track.has_lyrics && !!track.lyrics;
              const credits = track.track_credits || [];
              const samples = track.track_samples || [];
              const aiDecls = track.track_ai_declarations || [];

              // Group credits by type
              const lyricists = credits.filter((c) => c.credit_type === 'lyricist').map((c) => c.name);
              const composers = credits.filter((c) => c.credit_type === 'composer').map((c) => c.name);
              const performers = credits.filter((c) => c.credit_type === 'performer').map((c) => `${c.name}${c.role ? ` (${c.role})` : ''}`);
              const producers = credits.filter((c) => c.credit_type === 'producer').map((c) => c.name);

              return (
                <div key={track.id} className="admin-track-card">
                  {/* Track Header */}
                  <div className="admin-track-card-header">
                    <span className="admin-track-num-badge">TRACK #{track.track_number}</span>
                    <div className="admin-track-card-title">
                      {track.title}
                      {track.version_type && track.version_type !== 'Original' && (
                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginLeft: 6 }}>
                          ({track.version_type})
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span className="admin-sub-chip">{track.vocal_type || 'Vocal'}</span>
                      <span className="admin-sub-chip">{track.language || 'English'}</span>
                      {track.explicit && <span className="admin-sub-chip highlight" style={{ color: '#ef4444' }}>EXPLICIT</span>}
                      <span className="admin-sub-chip">
                        {track.audio_format?.toUpperCase() || 'WAV'} · {track.audio_sample_rate || 44100}Hz / {track.audio_bit_depth || 16}bit
                      </span>
                      {track.duration_seconds && (
                        <span className="admin-sub-chip" style={{ fontFamily: 'monospace' }}>
                          {formatTime(track.duration_seconds)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Audio Player & Download Controls (Section 4 & 5) */}
                  <div className="admin-player-bar">
                    {fileState.loading ? (
                      <div className="admin-audio-loading-badge">
                        <RefreshCw size={14} className="spin" />
                        <span>Verifying audio file in Storage…</span>
                      </div>
                    ) : fileState.exists && fileState.url ? (
                      <>
                        <button
                          type="button"
                          className="admin-play-btn"
                          onClick={() => togglePlayTrack(track)}
                          title={isPlaying ? 'Pause' : 'Play Track'}
                        >
                          {isPlaying ? <Pause size={16} /> : <Play size={16} style={{ marginLeft: 2 }} />}
                        </button>

                        <div className="admin-player-timeline">
                          <input
                            type="range"
                            className="admin-player-slider"
                            min={0}
                            max={isPlaying ? (duration || track.duration_seconds || 100) : (track.duration_seconds || 100)}
                            value={isPlaying ? currentTime : 0}
                            onChange={(e) => {
                              if (isPlaying && audioRef.current) {
                                audioRef.current.currentTime = Number(e.target.value);
                                setCurrentTime(Number(e.target.value));
                              }
                            }}
                          />
                          <span className="admin-player-time">
                            {isPlaying ? formatTime(currentTime) : '0:00'} / {formatTime(track.duration_seconds || (isPlaying ? duration : 0))}
                          </span>
                        </div>

                        <button
                          type="button"
                          className="admin-download-btn"
                          onClick={() => handleDownloadTrack(track)}
                          disabled={downloadingTrack[track.id]}
                          title="Download Master WAV file"
                        >
                          <Download size={13} />
                          <span>{downloadingTrack[track.id] ? 'Downloading…' : 'DOWNLOAD AUDIO'}</span>
                        </button>
                      </>
                    ) : (
                      <div className="admin-audio-unavailable-badge">
                        <AlertTriangle size={15} color="#ef4444" />
                        <span style={{ color: '#ef4444', fontWeight: 600 }}>
                          {fileState.message || 'Audio file is unavailable in Storage.'}
                        </span>
                        {fileState.path && (
                          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', marginLeft: 6 }}>
                            ({fileState.path})
                          </span>
                        )}
                      </div>
                    )}

                    {/* Track ISRC Input */}
                    <div className="admin-isrc-input-wrap" style={{ marginLeft: 'auto' }}>
                      <input
                        className="admin-isrc-input"
                        placeholder="Track ISRC"
                        defaultValue={track.isrc || ''}
                        onChange={(e) =>
                          setTrackIsrcVals((prev) => ({ ...prev, [track.id]: e.target.value }))
                        }
                      />
                      <button
                        type="button"
                        className="admin-btn admin-btn-ghost admin-btn-sm"
                        disabled={trackIsrcSaving[track.id]}
                        onClick={() => handleSaveTrackIsrc(track.id)}
                      >
                        {trackIsrcSaving[track.id] ? '…' : 'Save'}
                      </button>
                      {track.isrc && (
                        <span style={{ fontSize: 11, color: '#22c55e', fontFamily: 'monospace' }}>✓</span>
                      )}
                    </div>
                  </div>

                  {/* Track Metadata Grid */}
                  <div className="admin-track-meta-grid">
                    <div className="admin-meta-item">
                      <span className="admin-meta-label">Track Title</span>
                      <span className="admin-meta-val">{displayVal(track.title)}</span>
                    </div>
                    <div className="admin-meta-item">
                      <span className="admin-meta-label">Explicit Content</span>
                      <span className="admin-meta-val">{track.explicit ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="admin-meta-item">
                      <span className="admin-meta-label">Vocal / Non-Vocal</span>
                      <span className="admin-meta-val">{displayVal(track.vocal_type || 'Vocal')}</span>
                    </div>
                    <div className="admin-meta-item">
                      <span className="admin-meta-label">Language</span>
                      <span className="admin-meta-val">{displayVal(track.language)}</span>
                    </div>
                    <div className="admin-meta-item">
                      <span className="admin-meta-label">Country Recorded In</span>
                      <span className="admin-meta-val">{displayVal(track.country_recorded)}</span>
                    </div>
                    <div className="admin-meta-item">
                      <span className="admin-meta-label">Recording Year</span>
                      <span className="admin-meta-val">{track.recording_year ? String(track.recording_year) : 'Not provided'}</span>
                    </div>
                  </div>

                  {/* Credits Subsection */}
                  <div className="admin-track-credits-block">
                    <span className="admin-track-credits-title">Songwriter &amp; Production Credits</span>
                    <div className="admin-meta-grid" style={{ marginTop: 8 }}>
                      <div className="admin-meta-item">
                        <span className="admin-meta-label">Lyricist(s)</span>
                        <span className="admin-meta-val">{lyricists.length > 0 ? lyricists.join(', ') : 'Not provided'}</span>
                      </div>
                      <div className="admin-meta-item">
                        <span className="admin-meta-label">Composer(s)</span>
                        <span className="admin-meta-val">{composers.length > 0 ? composers.join(', ') : 'Not provided'}</span>
                      </div>
                      <div className="admin-meta-item">
                        <span className="admin-meta-label">Performer(s) &amp; Roles</span>
                        <span className="admin-meta-val">{performers.length > 0 ? performers.join(', ') : 'Not provided'}</span>
                      </div>
                      <div className="admin-meta-item">
                        <span className="admin-meta-label">Producer(s)</span>
                        <span className="admin-meta-val">{producers.length > 0 ? producers.join(', ') : 'Not provided'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Samples Subsection (Section 7) */}
                  <div className="admin-track-collapsible">
                    <span style={{ fontSize: 11, fontWeight: 600, color: samples.length > 0 ? '#f59e0b' : 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>
                      Samples: {samples.length > 0 ? 'Yes' : 'No'}
                    </span>
                    {samples.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                        {samples.map((s, sIdx) => (
                          <div key={s.id || sIdx} style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                            Sample #{sIdx + 1}:{' '}
                            <a href={s.sampled_track_url} target="_blank" rel="noreferrer" style={{ color: '#c4bcff', textDecoration: 'underline' }}>
                              {s.sampled_track_url}
                            </a>
                            {s.license_url && (
                              <span style={{ marginLeft: 10 }}>
                                • License:{' '}
                                <button
                                  type="button"
                                  className="admin-btn-link"
                                  onClick={() => handleViewDoc(s.license_url)}
                                  style={{ color: '#22c55e', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                >
                                  View License
                                </button>
                                {' / '}
                                <button
                                  type="button"
                                  className="admin-btn-link"
                                  onClick={() => handleDownloadDoc(s.license_url, `Sample_${sIdx + 1}_License.pdf`)}
                                  style={{ color: '#22c55e', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                >
                                  Download License
                                </button>
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                        No external samples declared.
                      </div>
                    )}
                  </div>

                  {/* AI Declarations Subsection (Section 7) */}
                  <div className="admin-track-collapsible">
                    <span style={{ fontSize: 11, fontWeight: 600, color: aiDecls.length > 0 ? '#38bdf8' : 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>
                      AI Usage: {aiDecls.length > 0 ? 'Yes' : 'No'}
                    </span>
                    {aiDecls.length > 0 ? (
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
                        {aiDecls.map((ai, aiIdx) => (
                          <div key={ai.id || aiIdx} style={{ display: 'flex', gap: 6 }}>
                            <button
                              type="button"
                              className="admin-btn admin-btn-ghost admin-btn-sm"
                              onClick={() => handleViewDoc(ai.declaration_file_path)}
                            >
                              <FileText size={13} />
                              <span>View AI Declaration ({ai.file_name || 'PDF'})</span>
                            </button>
                            <button
                              type="button"
                              className="admin-btn admin-btn-ghost admin-btn-sm"
                              onClick={() => handleDownloadDoc(ai.declaration_file_path, ai.file_name || 'AI_Declaration.pdf')}
                            >
                              <Download size={13} />
                              <span>Download</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                        No AI assistance declared.
                      </div>
                    )}
                  </div>

                  {/* Lyrics Subsection */}
                  <div className="admin-track-collapsible">
                    <button
                      type="button"
                      className="admin-collapsible-btn"
                      onClick={() =>
                        setExpandedLyrics((prev) => ({ ...prev, [track.id]: !prev[track.id] }))
                      }
                    >
                      <FileText size={12} />
                      <span>{hasLyrics ? (expandedLyrics[track.id] ? 'Hide Lyrics' : 'View Lyrics') : 'Lyrics: Not provided'}</span>
                    </button>
                    {hasLyrics && expandedLyrics[track.id] && (
                      <div className="admin-lyrics-box">{track.lyrics}</div>
                    )}
                  </div>
                </div>
              );
            })
        )}
      </div>

      {/* SECTION 7: ADMIN NOTES & REVIEW FEEDBACK */}
      {sub.notes && (
        <div className="admin-meta-section" style={{ background: 'rgba(245,158,11,0.06)', borderColor: 'rgba(245,158,11,0.2)' }}>
          <div className="admin-meta-section-title" style={{ color: '#fbbf24' }}>
            <AlertTriangle size={15} />
            <span>Admin Review Notes</span>
          </div>
          <div style={{ fontSize: 13, color: '#fbbf24', marginTop: 6, lineHeight: 1.5 }}>
            {sub.notes}
          </div>
        </div>
      )}

      {/* Cover art zoom modal */}
      {zoomCover && coverState.url && (
        <div className="admin-img-modal-backdrop" onClick={() => setZoomCover(false)}>
          <div className="admin-img-modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={coverState.url} alt="High resolution cover artwork" />
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button
                type="button"
                className="admin-btn admin-btn-primary admin-btn-sm"
                onClick={handleDownloadCover}
              >
                <Download size={13} /> Download 3000px Artwork
              </button>
              <a href={coverState.url} target="_blank" rel="noreferrer" className="admin-btn admin-btn-ghost admin-btn-sm">
                <ExternalLink size={13} /> Open in New Tab
              </a>
              <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => setZoomCover(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TAB: A&R Submissions
// ══════════════════════════════════════════════════════════════
function AdminSubmissions({ adminId }) {
  const [submissions, setSubmissions] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [statusModal, setStatusModal] = useState(null); // { submission, newStatus }
  const [statusReason, setStatusReason] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const load = useCallback(
    (pg = page, sf = statusFilter) => {
      setLoading(true);
      fetchAllSubmissions(sf, pg).then(({ data, count: c }) => {
        setSubmissions(data);
        setCount(c);
        setLoading(false);
      });
    },
    [page, statusFilter]
  );

  useEffect(() => { load(1, statusFilter); }, []); // eslint-disable-line

  const applyFilter = (sf) => { setStatusFilter(sf); setPage(1); load(1, sf); };

  const toggleExpand = (id) => setExpandedId(expandedId === id ? null : id);

  const openStatusModal = (submission, newStatus) => {
    setStatusModal({ submission, newStatus });
    setStatusReason('');
    setAlert(null);
  };

  const commitStatus = async () => {
    if (!statusModal) return;
    const { submission, newStatus } = statusModal;
    setStatusLoading(true);
    const { error } = await updateSubmissionStatus(
      submission.id,
      newStatus,
      statusReason,
      adminId,
      submission.status
    );
    setStatusLoading(false);
    if (error) {
      setAlert({ type: 'error', msg: 'Failed to update status: ' + (error.message || error) });
    } else {
      setStatusModal(null);
      load(page, statusFilter);
    }
  };

  // Section 12: Client search filter across Reg #, Artist name, Release title, and User ID
  const filteredSubmissions = useMemo(() => {
    if (!searchQuery.trim()) return submissions;
    const q = searchQuery.toLowerCase().trim();
    return submissions.filter((sub) => {
      const regNo = (sub.releases?.senritsux_registration_number || sub.reference_code || '').toLowerCase();
      const title = (sub.releases?.title || '').toLowerCase();
      const artistName = (sub.profiles?.stage_name || sub.profiles?.name || '').toLowerCase();
      const email = (sub.profiles?.email || '').toLowerCase();
      const userId = (sub.user_id || '').toLowerCase();
      return regNo.includes(q) || title.includes(q) || artistName.includes(q) || email.includes(q) || userId.includes(q);
    });
  }, [submissions, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div>
      <div className="admin-section-header">
        <div className="admin-section-title">A&amp;R Submissions</div>
        <div className="admin-filter-bar">
          {/* Section 12: Admin Search Bar */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, color: 'rgba(255,255,255,0.4)' }} />
            <input
              type="text"
              className="admin-form-input"
              style={{ paddingLeft: 30, height: 36, width: 270, fontSize: 12 }}
              placeholder="Search Reg #, Artist, Title, User ID…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                style={{ position: 'absolute', right: 8, background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}
              >
                <X size={12} />
              </button>
            )}
          </div>

          <select
            id="admin-submissions-filter"
            className="admin-select"
            value={statusFilter}
            onChange={(e) => applyFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="submitted">IN CHECK (Submitted)</option>
            <option value="under_review">In Review</option>
            <option value="approved">Approved</option>
            <option value="need_change">Need Change</option>
            <option value="rejected">Rejected</option>
            <option value="delivery">Delivery</option>
            <option value="delivered">Delivered</option>
            <option value="live">Live</option>
          </select>
          <button
            className="admin-btn admin-btn-ghost admin-btn-sm"
            onClick={() => load(page, statusFilter)}
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {alert && (
        <div className={`admin-alert admin-alert-${alert.type}`}>
          {alert.msg}
          <button style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }} onClick={() => setAlert(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Section 11: Admin Release Table */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Reg #</th>
              <th>Artist</th>
              <th>Release</th>
              <th>Type</th>
              <th>Tracks</th>
              <th>Audio</th>
              <th>Cover</th>
              <th>Submitted</th>
              <th>Status</th>
              <th>Actions</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows cols={11} rows={7} />
            ) : filteredSubmissions.length === 0 ? (
              <tr>
                <td colSpan={11}>
                  <div className="admin-empty">
                    <Inbox size={32} className="admin-empty-icon" />
                    <div className="admin-empty-title">No submissions found</div>
                    <div className="admin-empty-sub">Try changing the search query or status filter.</div>
                  </div>
                </td>
              </tr>
            ) : (
              filteredSubmissions.map((sub) => {
                const release = sub.releases;
                const artist = sub.profiles;
                const isExpanded = expandedId === sub.id;
                const nextStatuses = NEXT_STATUSES[sub.status] || [];
                const trackCount = release?.release_tracks?.length || 1;

                // Audio & Cover Health Checks
                const tracksWithAudio = (release?.release_tracks || []).filter(
                  (t) => t.storage_path || t.audio_processed_path
                ).length;
                const audioOk = tracksWithAudio >= trackCount && trackCount > 0;
                const hasCover = Boolean(
                  release?.cover_storage_path || release?.cover_processed_path || release?.cover_url
                );

                return (
                  <React.Fragment key={sub.id}>
                    <tr>
                      {/* Reg # */}
                      <td>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#c4bcff', fontWeight: 600 }}>
                          {release?.senritsux_registration_number || sub.reference_code || ', '}
                        </span>
                      </td>

                      {/* Artist */}
                      <td>
                        <div className="admin-user-cell">
                          <AdminAvatar profile={artist} size={28} />
                          <div>
                            <div className="admin-user-name">{artist?.stage_name || artist?.name || ', '}</div>
                            <div className="admin-user-sub">{artist?.email || ''}</div>
                          </div>
                        </div>
                      </td>

                      {/* Release Title */}
                      <td>
                        <div className="admin-user-name" style={{ fontSize: 13, fontWeight: 600 }}>{release?.title || 'Untitled'}</div>
                        <div className="admin-user-sub">{release?.genre || ', '}</div>
                      </td>

                      {/* Type */}
                      <td style={{ textTransform: 'capitalize', fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
                        {release?.release_type || ', '}
                      </td>

                      {/* Dynamic Track Count (Section 8) */}
                      <td style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                        {trackCount} {trackCount === 1 ? 'Track' : 'Tracks'}
                      </td>

                      {/* Audio Health (Section 11) */}
                      <td>
                        <span className={`admin-health-badge ${audioOk ? 'ok' : 'warn'}`}>
                          {tracksWithAudio} / {trackCount} {audioOk ? '✓' : '⚠'}
                        </span>
                      </td>

                      {/* Cover Health (Section 11) */}
                      <td>
                        <span className={`admin-health-badge ${hasCover ? 'ok' : 'warn'}`}>
                          {hasCover ? '1 / 1 ✓' : '0 / 1 ⚠'}
                        </span>
                      </td>

                      {/* Submitted Date */}
                      <td style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
                        {sub.submitted_at
                          ? new Date(sub.submitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                          : ', '}
                      </td>

                      {/* Status */}
                      <td><StatusBadge status={sub.status} /></td>

                      {/* Actions */}
                      <td>
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          {nextStatuses.map((ns) => (
                            <button
                              key={ns}
                              className={`admin-btn admin-btn-sm ${
                                ns === 'rejected'     ? 'admin-btn-danger' :
                                ns === 'approved'     ? 'admin-btn-success' :
                                ns === 'delivered'    ? 'admin-btn-success' :
                                ns === 'need_change'  ? 'admin-btn-ghost' :
                                'admin-btn-ghost'
                              }`}
                              onClick={() => openStatusModal(sub, ns)}
                            >
                              {STATUS_CONFIG[ns]?.icon} {STATUS_CONFIG[ns]?.label || ns}
                            </button>
                          ))}
                        </div>
                      </td>

                      {/* Expand Toggle */}
                      <td>
                        <button
                          type="button"
                          className="admin-expand-btn"
                          onClick={() => toggleExpand(sub.id)}
                          aria-label={isExpanded ? 'Collapse submission detail' : 'Expand submission detail'}
                        >
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </td>
                    </tr>

                    {/* Master Expanded Detail Card */}
                    {isExpanded && (
                      <tr className="admin-expand-row">
                        <td colSpan={11}>
                          <AdminSubmissionDetail
                            sub={sub}
                            adminId={adminId}
                            onRefresh={() => load(page, statusFilter)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="admin-pagination">
          <span>{count} total</span>
          <div className="admin-pagination-controls">
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={page <= 1}
              onClick={() => { setPage(page - 1); load(page - 1, statusFilter); }}
            >
              <ChevronLeft size={14} />
            </button>
            <span style={{ fontSize: 13 }}>Page {page} / {totalPages}</span>
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={page >= totalPages}
              onClick={() => { setPage(page + 1); load(page + 1, statusFilter); }}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Status Change Modal */}
      <Modal
        open={!!statusModal}
        onClose={() => setStatusModal(null)}
        title={`Move to: ${STATUS_CONFIG[statusModal?.newStatus]?.label || statusModal?.newStatus}`}
      >
        {statusModal && (
          <>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, marginBottom: 18 }}>
              Release: <strong style={{ color: '#e8e8f0' }}>{statusModal.submission.releases?.title}</strong>
              <br />
              <span style={{ fontFamily: 'monospace', fontSize: 11 }}>
                {statusModal.submission.releases?.senritsux_registration_number || statusModal.submission.reference_code}
              </span>
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <StatusBadge status={statusModal.submission.status} />
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>→</span>
              <StatusBadge status={statusModal.newStatus} />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="admin-status-reason">
                Notes / Reason
                {['rejected', 'need_change'].includes(statusModal.newStatus) && (
                  <span style={{ color: '#ef4444', marginLeft: 4 }}>*required</span>
                )}
              </label>
              <textarea
                id="admin-status-reason"
                className="admin-form-input admin-form-textarea"
                placeholder="Explain the decision or provide feedback to the artist…"
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
              />
            </div>
            {alert && <div className={`admin-alert admin-alert-${alert.type}`}>{alert.msg}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="admin-btn admin-btn-ghost" onClick={() => setStatusModal(null)} disabled={statusLoading}>
                Cancel
              </button>
              <button
                className={`admin-btn ${statusModal.newStatus === 'rejected' ? 'admin-btn-danger' : statusModal.newStatus === 'approved' || statusModal.newStatus === 'delivered' ? 'admin-btn-success' : 'admin-btn-primary'}`}
                onClick={commitStatus}
                disabled={statusLoading || (['rejected', 'need_change'].includes(statusModal.newStatus) && !statusReason.trim())}
              >
                {statusLoading ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TAB: Release Storage Index (Dedicated Storage Architecture Table)
// ══════════════════════════════════════════════════════════════
function AdminStorageIndex({ adminId }) {
  const [indexItems, setIndexItems] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedReg, setExpandedReg] = useState(null);
  const [alert, setAlert] = useState(null);
  const [migratingReg, setMigratingReg] = useState({});

  const loadIndex = useCallback((pg = page, s = search, sf = statusFilter) => {
    setLoading(true);
    fetchReleaseStorageIndex({ search: s, statusFilter: sf, page: pg, limit: PAGE_SIZE }).then(({ data, count: c, error }) => {
      if (error) {
        setAlert({ type: 'error', msg: 'Failed to load Storage Index: ' + error.message });
      } else {
        setIndexItems(data);
        setCount(c);
      }
      setLoading(false);
    });
  }, [page, search, statusFilter]);

  useEffect(() => {
    loadIndex(1, search, statusFilter);
  }, []); // eslint-disable-line

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    loadIndex(1, search, statusFilter);
  };

  const handleMigrate = async (releaseId, regNo) => {
    if (!releaseId) return;
    setMigratingReg((prev) => ({ ...prev, [regNo]: true }));
    const res = await migrateReleaseToNewStorage(releaseId, adminId);
    setMigratingReg((prev) => ({ ...prev, [regNo]: false }));
    if (res.success) {
      setAlert({ type: 'success', msg: `Storage successfully migrated for ${regNo}.` });
      loadIndex(page, search, statusFilter);
    } else {
      setAlert({ type: 'error', msg: `Migration notice for ${regNo}: ${res.error}` });
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div>
      <div className="admin-section-header">
        <div>
          <div className="admin-section-title">Release Storage Index</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
            {'Standardized release-based Storage architecture · tracks/{regNo} & covers/{regNo}'}
          </div>
        </div>
        <div className="admin-filter-bar">
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: 6 }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, color: 'rgba(255,255,255,0.4)' }} />
              <input
                type="text"
                className="admin-form-input"
                style={{ paddingLeft: 30, height: 36, width: 280, fontSize: 12 }}
                placeholder="Search Reg # (SX-...), Artist, Title, User ID…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); loadIndex(1, '', statusFilter); }}
                  style={{ position: 'absolute', right: 8, background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <button type="submit" className="admin-btn admin-btn-primary admin-btn-sm">
              Search
            </button>
          </form>

          <select
            className="admin-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); loadIndex(1, search, e.target.value); }}
          >
            <option value="">All Storage Statuses</option>
            <option value="uploaded">Uploaded</option>
            <option value="verified">Verified</option>
            <option value="missing">Missing</option>
            <option value="pending">Pending</option>
          </select>

          <button
            className="admin-btn admin-btn-ghost admin-btn-sm"
            onClick={() => loadIndex(page, search, statusFilter)}
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {alert && (
        <div className={`admin-alert admin-alert-${alert.type}`}>
          {alert.msg}
          <button style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }} onClick={() => setAlert(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Section 11: Real Admin Release Table from real DB */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Reg No.</th>
              <th>Artist</th>
              <th>Release</th>
              <th>Type</th>
              <th>Tracks</th>
              <th>Audio</th>
              <th>Cover</th>
              <th>Status</th>
              <th>Actions</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows cols={10} rows={7} />
            ) : indexItems.length === 0 ? (
              <tr>
                <td colSpan={10}>
                  <div className="admin-empty">
                    <HardDrive size={32} className="admin-empty-icon" />
                    <div className="admin-empty-title">No release storage records found</div>
                    <div className="admin-empty-sub">Try adjusting your search or filters.</div>
                  </div>
                </td>
              </tr>
            ) : (
              indexItems.map((item) => {
                const isExpanded = expandedReg === item.registration_number;
                const artist = item.releases?.profiles;
                const audioOk = item.audio_status === 'uploaded' || item.audio_status === 'verified';
                const coverOk = item.cover_status === 'uploaded' || item.cover_status === 'verified';

                return (
                  <React.Fragment key={item.id}>
                    <tr>
                      {/* Reg No. */}
                      <td>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#a78bfa', fontWeight: 600 }}>
                          {item.registration_number}
                        </span>
                      </td>

                      {/* Artist */}
                      <td>
                        <div className="admin-user-cell">
                          <AdminAvatar profile={artist} size={28} />
                          <div>
                            <div className="admin-user-name">{artist?.stage_name || artist?.name || 'Artist'}</div>
                            <div className="admin-user-sub">{artist?.email || ''}</div>
                          </div>
                        </div>
                      </td>

                      {/* Release */}
                      <td>
                        <div className="admin-user-name" style={{ fontSize: 13, fontWeight: 600 }}>{item.release_title}</div>
                        <div className="admin-user-sub">{item.releases?.genre || ', '}</div>
                      </td>

                      {/* Type */}
                      <td style={{ textTransform: 'capitalize', fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
                        {item.release_type}
                      </td>

                      {/* Dynamic Track Count */}
                      <td style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                        {item.track_count} {item.track_count === 1 ? 'Track' : 'Tracks'}
                      </td>

                      {/* Audio Health Check (Section 11) */}
                      <td>
                        <span className={`admin-health-badge ${audioOk ? 'ok' : 'warn'}`}>
                          {item.track_count} / {item.track_count} {audioOk ? '✓' : '⚠'}
                        </span>
                      </td>

                      {/* Cover Health Check (Section 11) */}
                      <td>
                        <span className={`admin-health-badge ${coverOk ? 'ok' : 'warn'}`}>
                          {coverOk ? '1 / 1 ✓' : '0 / 1 ⚠'}
                        </span>
                      </td>

                      {/* Status */}
                      <td>
                        <StatusBadge status={item.releases?.status || 'submitted'} />
                      </td>

                      {/* Actions */}
                      <td>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button
                            type="button"
                            className="admin-btn admin-btn-ghost admin-btn-sm"
                            onClick={() => handleMigrate(item.release_id, item.registration_number)}
                            disabled={migratingReg[item.registration_number]}
                            title="Sync assets to tracks and covers buckets"
                          >
                            <RefreshCw size={11} className={migratingReg[item.registration_number] ? 'spin' : ''} />
                            <span>{migratingReg[item.registration_number] ? 'Syncing…' : 'Sync'}</span>
                          </button>
                          <button
                            type="button"
                            className="admin-btn admin-btn-primary admin-btn-sm"
                            onClick={() => setExpandedReg(isExpanded ? null : item.registration_number)}
                          >
                            {isExpanded ? 'Hide' : 'Inspect'}
                          </button>
                        </div>
                      </td>

                      {/* Expand Toggle */}
                      <td>
                        <button
                          type="button"
                          className="admin-expand-btn"
                          onClick={() => setExpandedReg(isExpanded ? null : item.registration_number)}
                        >
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </td>
                    </tr>

                    {/* Section 10: Expanded Storage File Detail */}
                    {isExpanded && item.releases && (
                      <tr className="admin-expand-row">
                        <td colSpan={10}>
                          <AdminSubmissionDetail
                            sub={{
                              id: item.release_id,
                              user_id: item.user_id,
                              release_id: item.release_id,
                              reference_code: item.registration_number,
                              status: item.releases?.status || 'submitted',
                              submitted_at: item.submitted_at,
                              releases: item.releases,
                              profiles: artist,
                            }}
                            adminId={adminId}
                            onRefresh={() => loadIndex(page, search, statusFilter)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="admin-pagination">
          <span>{count} release records</span>
          <div className="admin-pagination-controls">
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={page <= 1}
              onClick={() => { setPage(page - 1); loadIndex(page - 1, search, statusFilter); }}
            >
              <ChevronLeft size={14} />
            </button>
            <span style={{ fontSize: 13 }}>Page {page} / {totalPages}</span>
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={page >= totalPages}
              onClick={() => { setPage(page + 1); loadIndex(page + 1, search, statusFilter); }}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// TAB: Financials (audit log ,  financial actions only)
// ══════════════════════════════════════════════════════════════
function AdminFinancials({ adminId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchAuditLogs({ action: 'financial_' }, 1).then(({ data }) => {
      setLogs(data);
      setLoading(false);
    });
  }, []);

  return (
    <div>
      <div className="admin-section-header">
        <div>
          <div className="admin-section-title">Financials</div>
        </div>
      </div>
      <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, marginBottom: 20, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)', borderRadius: 10, padding: '12px 16px' }}>
        <AlertTriangle size={14} style={{ display: 'inline', marginRight: 8, color: '#f59e0b', verticalAlign: 'middle' }} />
        Financial records are immutable audit entries. To record a financial correction, add a new audit entry ,  do not edit existing records.
        <br />
        Full wallet / royalty payout system is a planned v2 feature.
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Admin</th>
              <th>Old Value</th>
              <th>New Value</th>
              <th>Reason</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows cols={6} rows={5} />
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="admin-empty">
                    <BarChart2 size={32} className="admin-empty-icon" />
                    <div className="admin-empty-title">No financial records yet</div>
                    <div className="admin-empty-sub">Financial audit entries will appear here.</div>
                  </div>
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id}>
                  <td><span className="admin-action-pill">{log.action}</span></td>
                  <td>
                    <div className="admin-user-cell">
                      <AdminAvatar profile={log.profiles} size={26} />
                      <span style={{ fontSize: 12 }}>{log.profiles?.stage_name || log.profiles?.name || ', '}</span>
                    </div>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                    {log.old_value ? JSON.stringify(log.old_value) : ', '}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>
                    {log.new_value ? JSON.stringify(log.new_value) : ', '}
                  </td>
                  <td style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{log.reason || ', '}</td>
                  <td style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>
                    {new Date(log.created_at).toLocaleString('en-IN')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// TAB: Notifications
// ══════════════════════════════════════════════════════════════
function AdminNotifications({ adminId }) {
  const [recipientType, setRecipientType] = useState('single'); // 'single' | 'broadcast'
  const [recipientId, setRecipientId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState(null);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      setAlert({ type: 'error', msg: 'Title and message are required.' });
      return;
    }
    if (recipientType === 'single' && !recipientId.trim()) {
      setAlert({ type: 'error', msg: 'Please enter a user ID for single-user notification.' });
      return;
    }
    setSending(true);
    setAlert(null);
    const targetId = recipientType === 'broadcast' ? null : recipientId.trim();
    const { error } = await sendAdminNotification(targetId, title.trim(), body.trim(), adminId);
    setSending(false);
    if (error) {
      setAlert({ type: 'error', msg: 'Failed to send: ' + (error.message || error) });
    } else {
      setAlert({ type: 'success', msg: recipientType === 'broadcast' ? 'Broadcast sent to all users.' : 'Notification sent.' });
      setTitle('');
      setBody('');
      setRecipientId('');
    }
  };

  return (
    <div>
      <div className="admin-section-header">
        <div className="admin-section-title">Send Notification</div>
      </div>

      <div className="admin-notif-compose">
        {alert && (
          <div className={`admin-alert admin-alert-${alert.type}`} style={{ marginBottom: 16 }}>
            {alert.msg}
          </div>
        )}

        <div className="admin-form-group">
          <label className="admin-form-label">Recipient</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 2 }}>
            <button
              className={`admin-btn admin-btn-sm ${recipientType === 'single' ? 'admin-btn-primary' : 'admin-btn-ghost'}`}
              onClick={() => setRecipientType('single')}
            >
              <User size={13} /> Single User
            </button>
            <button
              className={`admin-btn admin-btn-sm ${recipientType === 'broadcast' ? 'admin-btn-primary' : 'admin-btn-ghost'}`}
              onClick={() => setRecipientType('broadcast')}
            >
              <Globe size={13} /> Broadcast (All Users)
            </button>
          </div>
        </div>

        {recipientType === 'single' && (
          <div className="admin-form-group">
            <label className="admin-form-label" htmlFor="admin-notif-userid">User ID (profiles.id UUID)</label>
            <input
              id="admin-notif-userid"
              className="admin-form-input"
              placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
              value={recipientId}
              onChange={(e) => setRecipientId(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
            <div className="admin-form-hint">Copy the user ID from the Artists tab.</div>
          </div>
        )}

        {recipientType === 'broadcast' && (
          <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#fbbf24', marginBottom: 16 }}>
            <AlertTriangle size={12} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
            This will send a notification to every registered user. Use with care.
          </div>
        )}

        <div className="admin-form-group">
          <label className="admin-form-label" htmlFor="admin-notif-title">Title</label>
          <input
            id="admin-notif-title"
            className="admin-form-input"
            placeholder="Notification title…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
          />
        </div>

        <div className="admin-form-group">
          <label className="admin-form-label" htmlFor="admin-notif-body">Message</label>
          <textarea
            id="admin-notif-body"
            className="admin-form-input admin-form-textarea"
            placeholder="Write the notification message…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={500}
          />
          <div className="admin-form-hint">{body.length}/500 characters</div>
        </div>

        <button
          id="admin-send-notification-btn"
          className="admin-btn admin-btn-primary"
          onClick={handleSend}
          disabled={sending}
          style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}
        >
          <Send size={14} />
          {sending ? 'Sending…' : recipientType === 'broadcast' ? 'Send Broadcast' : 'Send Notification'}
        </button>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// TAB: Audit Log
// ══════════════════════════════════════════════════════════════
function AdminAuditLog() {
  const [logs, setLogs] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    (pg = page) => {
      setLoading(true);
      fetchAuditLogs({}, pg).then(({ data, count: c }) => {
        setLogs(data);
        setCount(c);
        setLoading(false);
      });
    },
    [page]
  );

  useEffect(() => { load(1); }, []); // eslint-disable-line

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div>
      <div className="admin-section-header">
        <div className="admin-section-title">Audit Log</div>
        <button className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => load(page)}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, marginBottom: 14 }}>
        Immutable ,  admin actions are append-only and cannot be edited or deleted.
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Target</th>
              <th>Admin</th>
              <th>Old → New</th>
              <th>Reason</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows cols={6} rows={8} />
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="admin-empty">
                    <ScrollText size={32} className="admin-empty-icon" />
                    <div className="admin-empty-title">No audit log entries yet</div>
                    <div className="admin-empty-sub">Admin actions will be recorded here.</div>
                  </div>
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id}>
                  <td><span className="admin-action-pill">{log.action}</span></td>
                  <td style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' }}>
                    <div>{log.target_type}</div>
                    <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>{log.target_id?.slice(0, 8) || ', '}…</div>
                  </td>
                  <td>
                    <div className="admin-user-cell">
                      <AdminAvatar profile={log.profiles} size={26} />
                      <span style={{ fontSize: 12 }}>{log.profiles?.stage_name || log.profiles?.name || ', '}</span>
                    </div>
                  </td>
                  <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'rgba(255,255,255,0.5)' }}>
                    {log.old_value
                      ? <span style={{ color: 'rgba(239,68,68,0.7)' }}>{JSON.stringify(log.old_value)}</span>
                      : ', '}
                    {(log.old_value && log.new_value) ? <span style={{ color: 'rgba(255,255,255,0.2)', margin: '0 4px' }}>→</span> : null}
                    {log.new_value
                      ? <span style={{ color: 'rgba(34,197,94,0.8)' }}>{JSON.stringify(log.new_value)}</span>
                      : ''}
                  </td>
                  <td style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', maxWidth: 200, wordBreak: 'break-word' }}>
                    {log.reason || ', '}
                  </td>
                  <td style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', whiteSpace: 'nowrap' }}>
                    {new Date(log.created_at).toLocaleString('en-IN')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="admin-pagination">
          <span>{count} total entries</span>
          <div className="admin-pagination-controls">
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={page <= 1}
              onClick={() => { setPage(page - 1); load(page - 1); }}
            >
              <ChevronLeft size={14} />
            </button>
            <span style={{ fontSize: 13 }}>Page {page} / {totalPages}</span>
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={page >= totalPages}
              onClick={() => { setPage(page + 1); load(page + 1); }}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// TAB: Contact Messages
// ══════════════════════════════════════════════════════════════
function AdminContactMessages({ adminId }) {
  const [messages, setMessages] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [notesInput, setNotesInput] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(async (p = 1, stat = statusFilter, q = searchQuery) => {
    setLoading(true);
    const { data, count: total, error } = await fetchContactMessages({
      page: p,
      pageSize: PAGE_SIZE,
      status: stat,
      search: q,
    });
    if (!error) {
      setMessages(data || []);
      setCount(total || 0);
    }
    setLoading(false);
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    load(page, statusFilter, searchQuery);
    const interval = setInterval(() => {
      load(page, statusFilter, searchQuery);
    }, 20000);
    return () => clearInterval(interval);
  }, [load, page, statusFilter, searchQuery]);

  const handleStatusChange = async (msgId, newStatus) => {
    setUpdatingId(msgId);
    const { error } = await updateContactMessageStatus(msgId, newStatus, null, adminId);
    if (!error) {
      setMessages((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, status: newStatus } : m))
      );
      if (selectedMsg && selectedMsg.id === msgId) {
        setSelectedMsg((prev) => ({ ...prev, status: newStatus }));
      }
    }
    setUpdatingId(null);
  };

  const handleSaveNotes = async () => {
    if (!selectedMsg) return;
    setSavingNotes(true);
    const { error } = await updateContactMessageStatus(selectedMsg.id, selectedMsg.status, notesInput, adminId);
    if (!error) {
      setSelectedMsg((prev) => ({ ...prev, admin_notes: notesInput }));
      setMessages((prev) =>
        prev.map((m) => (m.id === selectedMsg.id ? { ...m, admin_notes: notesInput } : m))
      );
    }
    setSavingNotes(false);
  };

  const openDetail = (msg) => {
    setSelectedMsg(msg);
    setNotesInput(msg.admin_notes || '');
    if (msg.status === 'unread') {
      handleStatusChange(msg.id, 'read');
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div>
      <div className="admin-section-header">
        <div>
          <div className="admin-section-title">Direct Contact Messages</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
            Inquiries submitted via the Contact page. Notifications dispatched to senritsux.in@gmail.com
          </div>
        </div>
        <div className="admin-filter-bar">
          <div className="admin-search-box">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search by name, email, subject..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <button
            className="admin-btn admin-btn-ghost admin-btn-sm"
            onClick={() => load(page, statusFilter, searchQuery)}
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      <div className="admin-filter-tabs" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['all', 'unread', 'read', 'replied', 'archived'].map((st) => (
          <button
            key={st}
            className={`admin-btn admin-btn-sm ${statusFilter === st ? 'admin-btn-primary' : 'admin-btn-ghost'}`}
            onClick={() => {
              setStatusFilter(st);
              setPage(1);
            }}
          >
            {st.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="admin-table-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Sender</th>
              <th>Subject</th>
              <th>Message Preview</th>
              <th>Date</th>
              <th>Status</th>
              <th>Email Delivery</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows cols={7} rows={6} />
            ) : messages.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="admin-empty">
                    <Mail size={32} className="admin-empty-icon" />
                    <div className="admin-empty-title">No contact messages found</div>
                    <div className="admin-empty-sub">
                      {searchQuery ? 'No messages match your search query.' : 'New messages submitted through the contact form will appear here.'}
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              messages.map((msg) => (
                <tr key={msg.id} style={{ background: msg.status === 'unread' ? 'rgba(217, 251, 6, 0.03)' : 'transparent' }}>
                  <td>
                    <div style={{ fontWeight: 600, color: '#fff' }}>{msg.name}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{msg.email}</div>
                    <div style={{ fontSize: 11, marginTop: 2, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span className="admin-badge gray" style={{ fontSize: 10, padding: '2px 6px' }}>{msg.role || 'Guest'}</span>
                      {msg.user_id ? (
                        <span className="admin-badge blue" style={{ fontSize: 10, padding: '2px 6px' }}>Registered</span>
                      ) : (
                        <span className="admin-badge gray" style={{ fontSize: 10, padding: '2px 6px' }}>Visitor</span>
                      )}
                    </div>
                  </td>
                  <td style={{ maxWidth: 160, fontWeight: 500, color: '#fff' }}>
                    {msg.subject || 'Direct Inquiry'}
                  </td>
                  <td style={{ maxWidth: 260 }}>
                    <div
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: 13,
                        cursor: 'pointer',
                      }}
                      onClick={() => openDetail(msg)}
                      title="Click to view full message"
                    >
                      {msg.message}
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>
                    {new Date(msg.created_at).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td>
                    <span className={`admin-badge ${msg.status === 'unread' ? 'amber' : msg.status === 'replied' ? 'green' : msg.status === 'read' ? 'blue' : 'gray'}`}>
                      {msg.status.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <span className={`admin-badge ${msg.email_status === 'email_sent' ? 'green' : msg.email_status === 'email_failed' ? 'red' : 'gray'}`} style={{ fontSize: 10 }}>
                      {msg.email_status === 'email_sent' ? 'SENT' : msg.email_status === 'email_failed' ? 'FAILED' : 'PENDING'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="admin-btn admin-btn-ghost admin-btn-sm"
                        onClick={() => openDetail(msg)}
                        title="View Full Message"
                      >
                        <Eye size={13} />
                      </button>
                      {msg.status !== 'replied' && (
                        <button
                          className="admin-btn admin-btn-ghost admin-btn-sm"
                          disabled={updatingId === msg.id}
                          onClick={() => handleStatusChange(msg.id, 'replied')}
                          title="Mark as Replied"
                        >
                          <CheckCircle size={13} />
                        </button>
                      )}
                      {msg.status !== 'archived' && (
                        <button
                          className="admin-btn admin-btn-ghost admin-btn-sm"
                          disabled={updatingId === msg.id}
                          onClick={() => handleStatusChange(msg.id, 'archived')}
                          title="Archive Message"
                        >
                          <Archive size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="admin-pagination">
          <span>{count} total messages</span>
          <div className="admin-pagination-controls">
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={page <= 1}
              onClick={() => { setPage(page - 1); load(page - 1); }}
            >
              <ChevronLeft size={14} />
            </button>
            <span style={{ fontSize: 13 }}>Page {page} / {totalPages}</span>
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={page >= totalPages}
              onClick={() => { setPage(page + 1); load(page + 1); }}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Message Details Modal */}
      {selectedMsg && (
        <Modal
          open={Boolean(selectedMsg)}
          onClose={() => setSelectedMsg(null)}
          title={`Message from ${selectedMsg.name}`}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, background: 'rgba(255,255,255,0.03)', padding: 14, borderRadius: 8 }}>
              <div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>From</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginTop: 2 }}>{selectedMsg.name}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{selectedMsg.email}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Role / Status</div>
                <div style={{ fontSize: 13, color: '#fff', marginTop: 2 }}>{selectedMsg.role || 'Not specified'}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                  {selectedMsg.user_id ? `User ID: ${selectedMsg.user_id}` : 'Guest User'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Subject</div>
                <div style={{ fontSize: 13, color: '#fff', marginTop: 2, fontWeight: 500 }}>{selectedMsg.subject || 'Direct Inquiry'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Submitted At</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                  {new Date(selectedMsg.created_at).toLocaleString('en-IN')}
                </div>
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 6, textTransform: 'uppercase' }}>Message</div>
              <div style={{
                background: 'rgba(0,0,0,0.25)',
                border: '1px solid rgba(255,255,255,0.1)',
                padding: '14px 16px',
                borderRadius: 8,
                fontSize: 14,
                lineHeight: 1.6,
                color: '#fff',
                whiteSpace: 'pre-wrap',
                maxHeight: 240,
                overflowY: 'auto'
              }}>
                {selectedMsg.message}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 6, textTransform: 'uppercase' }}>Internal Admin Notes</div>
              <textarea
                rows={3}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6,
                  color: '#fff',
                  padding: '8px 12px',
                  fontSize: 13,
                  resize: 'vertical'
                }}
                placeholder="Add internal notes about this inquiry..."
                value={notesInput}
                onChange={(e) => setNotesInput(e.target.value)}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                <button
                  className="admin-btn admin-btn-sm admin-btn-primary"
                  disabled={savingNotes}
                  onClick={handleSaveNotes}
                >
                  {savingNotes ? 'Saving...' : 'Save Notes'}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 14 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Set Status:</span>
                {['unread', 'read', 'replied', 'archived'].map((st) => (
                  <button
                    key={st}
                    className={`admin-btn admin-btn-sm ${selectedMsg.status === st ? 'admin-btn-primary' : 'admin-btn-ghost'}`}
                    disabled={updatingId === selectedMsg.id}
                    onClick={() => handleStatusChange(selectedMsg.id, st)}
                  >
                    {st.toUpperCase()}
                  </button>
                ))}
              </div>
              <button
                className="admin-btn admin-btn-ghost admin-btn-sm"
                onClick={() => setSelectedMsg(null)}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// TAB: OAC Requests (YouTube Official Artist Channel)
// ══════════════════════════════════════════════════════════════
function AdminOacRequests({ adminId }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedReq, setSelectedReq] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [actionError, setActionError] = useState(null);

  const loadRequests = useCallback(async (pg = 1, st = statusFilter, q = searchQuery) => {
    setLoading(true);
    try {
      const { data, count: total, totalPages: tp, error } = await fetchAdminOacRequests({
        page: pg,
        pageSize: PAGE_SIZE,
        status: st,
        search: q,
      });
      if (data) {
        setRequests(data);
        setCount(total || 0);
        setTotalPages(tp || 1);
      }
      if (error) {
        console.error('[AdminOacRequests] Load error:', error);
      }
    } catch (err) {
      console.error('[AdminOacRequests] Unexpected load error:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    loadRequests(page, statusFilter, searchQuery);
  }, [page, statusFilter, searchQuery, loadRequests]);

  // Realtime subscription to oac_requests table
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    const channelTopic = `admin_oac_realtime_${Date.now()}`;
    const channel = supabase
      .channel(channelTopic)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'oac_requests' }, () => {
        loadRequests(page, statusFilter, searchQuery);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [page, statusFilter, searchQuery, loadRequests]);

  const openReviewModal = (req) => {
    setSelectedReq(req);
    setReviewNotes(req.review_notes || '');
    setRejectionReason(req.rejection_reason || '');
    setActionError(null);
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedReq) return;
    if (newStatus === 'rejected' && !rejectionReason.trim()) {
      setActionError('Please provide a specific rejection reason so the artist knows what requirements were missed.');
      return;
    }

    setSavingStatus(true);
    setActionError(null);
    try {
      const { data, error } = await updateOacRequestStatus({
        requestId: selectedReq.id,
        status: newStatus,
        reviewNotes: reviewNotes.trim(),
        rejectionReason: newStatus === 'rejected' ? rejectionReason.trim() : null,
        adminId,
      });

      if (error) {
        setActionError(error.message || 'Failed to update OAC request status.');
        return;
      }

      setSelectedReq(null);
      loadRequests(page, statusFilter, searchQuery);
    } catch (err) {
      console.error('[AdminOacRequests] Status update error:', err);
      setActionError(err.message || 'Unexpected error occurred.');
    } finally {
      setSavingStatus(false);
    }
  };

  const getStatusBadge = (st) => {
    switch (st) {
      case 'pending':
        return <span className="admin-badge pending"><Clock size={11} /> PENDING SCREENING</span>;
      case 'under_review':
        return <span className="admin-badge under_review"><RefreshCw size={11} /> UNDER REVIEW</span>;
      case 'approved':
        return <span className="admin-badge approved"><CheckCircle size={11} /> APPROVED</span>;
      case 'rejected':
        return <span className="admin-badge rejected"><XCircle size={11} /> REJECTED</span>;
      default:
        return <span className="admin-badge gray">{st?.toUpperCase()}</span>;
    }
  };

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <div>
          <div className="admin-section-title">Official Artist Channel (OAC) Requests</div>
          <div className="admin-section-desc">
            Review, screen, and approve artist requests for YouTube Official Artist Channel upgrades via SenritsuX.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div className="admin-search-bar" style={{ width: 280 }}>
            <Search size={14} className="admin-search-icon" />
            <input
              type="text"
              placeholder="Search artist, release, channel..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <button
            className="admin-btn admin-btn-ghost admin-btn-sm"
            onClick={() => loadRequests(page, statusFilter, searchQuery)}
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="admin-filter-tabs" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { id: 'all', label: 'ALL' },
          { id: 'pending', label: 'PENDING' },
          { id: 'under_review', label: 'UNDER REVIEW' },
          { id: 'approved', label: 'APPROVED' },
          { id: 'rejected', label: 'REJECTED' },
        ].map((tab) => (
          <button
            key={tab.id}
            className={`admin-btn admin-btn-sm ${statusFilter === tab.id ? 'admin-btn-primary' : 'admin-btn-ghost'}`}
            onClick={() => {
              setStatusFilter(tab.id);
              setPage(1);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Requests Table */}
      <div className="admin-table-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Artist</th>
              <th>Qualifying Release</th>
              <th>Topic Track</th>
              <th>YouTube Channel</th>
              <th>Date Submitted</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows cols={7} rows={6} />
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="admin-empty">
                    <Youtube size={36} className="admin-empty-icon" style={{ color: '#ef4444' }} />
                    <div className="admin-empty-title">No OAC requests found</div>
                    <div className="admin-empty-sub">
                      {searchQuery
                        ? 'No requests match your search criteria.'
                        : 'New Official Artist Channel requests submitted by eligible artists will appear here.'}
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              requests.map((req) => {
                const artistName = req.artist_profile?.stage_name || req.user_profile?.stage_name || req.user_profile?.name || 'Artist';
                const artistEmail = req.user_profile?.email || 'No email';
                const releaseTitle = req.release?.title || 'Release';
                const releaseReg = req.release?.registration_number || req.release?.upc || 'N/A';
                const primaryGenre = req.artist_profile?.genre || req.user_profile?.genre;
                const secondaryGenre = req.artist_profile?.secondary_genre || req.user_profile?.secondary_genre;

                return (
                  <tr
                    key={req.id}
                    style={{
                      background: req.status === 'pending' ? 'rgba(217, 251, 6, 0.03)' : 'transparent',
                    }}
                  >
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <AdminAvatar profile={req.artist_profile || req.user_profile} size={34} />
                        <div>
                          <div style={{ fontWeight: 600, color: '#fff' }}>{artistName}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{artistEmail}</div>
                          {primaryGenre && (
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                              {primaryGenre}
                              {secondaryGenre ? ` · ${secondaryGenre}` : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, color: '#fff' }}>{releaseTitle}</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>Reg: {releaseReg}</div>
                      <div style={{ fontSize: 10, color: '#38bdf8', marginTop: 2 }}>
                        {req.release?.status?.toUpperCase() || 'DISTRIBUTED'}
                      </div>
                    </td>
                    <td>
                      {req.youtube_topic_url ? (
                        <a
                          href={req.youtube_topic_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="admin-btn admin-btn-ghost admin-btn-sm"
                          style={{ display: 'inline-flex', gap: 5, fontSize: 11 }}
                          title="Open YouTube Topic Track"
                        >
                          <Music size={12} /> View Track <ExternalLink size={10} />
                        </a>
                      ) : (
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>, </span>
                      )}
                    </td>
                    <td>
                      {req.youtube_channel_url ? (
                        <a
                          href={req.youtube_channel_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="admin-btn admin-btn-ghost admin-btn-sm"
                          style={{ display: 'inline-flex', gap: 5, fontSize: 11, color: '#ef4444' }}
                          title="Open YouTube Artist Channel"
                        >
                          <Youtube size={12} /> Channel <ExternalLink size={10} />
                        </a>
                      ) : (
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>, </span>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>
                      {new Date(req.created_at).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td>{getStatusBadge(req.status)}</td>
                    <td>
                      <button
                        className="admin-btn admin-btn-ghost admin-btn-sm"
                        onClick={() => openReviewModal(req)}
                        title="Review Request"
                      >
                        <Eye size={13} /> Review
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="admin-pagination">
          <span>{count} total OAC requests</span>
          <div className="admin-pagination-controls">
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={page <= 1}
              onClick={() => {
                setPage(page - 1);
                loadRequests(page - 1);
              }}
            >
              <ChevronLeft size={14} />
            </button>
            <span style={{ fontSize: 13 }}>Page {page} / {totalPages}</span>
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={page >= totalPages}
              onClick={() => {
                setPage(page + 1);
                loadRequests(page + 1);
              }}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {selectedReq && (
        <Modal
          open={!!selectedReq}
          onClose={() => setSelectedReq(null)}
          title="Review YouTube OAC Request"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {actionError && (
              <div
                style={{
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: 8,
                  padding: '10px 14px',
                  color: '#f87171',
                  fontSize: 13,
                }}
              >
                {actionError}
              </div>
            )}

            {/* Artist & Release Overview */}
            <div
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 10,
                padding: '14px 16px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>
                  Artist
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginTop: 3 }}>
                  {selectedReq.artist_profile?.stage_name || selectedReq.user_profile?.stage_name || selectedReq.user_profile?.name || 'Artist'}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                  {selectedReq.user_profile?.email}
                </div>
                {(selectedReq.artist_profile?.genre || selectedReq.user_profile?.genre) && (
                  <div style={{ fontSize: 11, color: '#d9fb06', marginTop: 4 }}>
                    {selectedReq.artist_profile?.genre || selectedReq.user_profile?.genre}
                    {(selectedReq.artist_profile?.secondary_genre || selectedReq.user_profile?.secondary_genre)
                      ? ` · ${selectedReq.artist_profile?.secondary_genre || selectedReq.user_profile?.secondary_genre}`
                      : ''}
                  </div>
                )}
              </div>

              <div>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>
                  Qualifying Release
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginTop: 3 }}>
                  {selectedReq.release?.title || 'Release'}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                  Reg: {selectedReq.release?.registration_number || 'N/A'}
                </div>
                <div style={{ fontSize: 11, color: '#38bdf8', marginTop: 4 }}>
                  Status: {selectedReq.release?.status?.toUpperCase() || 'DISTRIBUTED'}
                </div>
              </div>
            </div>

            {/* Verification Links */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: 600, marginBottom: 4 }}>
                  YouTube Topic Track Link
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="text"
                    readOnly
                    value={selectedReq.youtube_topic_url || ''}
                    className="admin-form-input"
                    style={{ fontSize: 12, color: '#38bdf8' }}
                  />
                  {selectedReq.youtube_topic_url && (
                    <a
                      href={selectedReq.youtube_topic_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="admin-btn admin-btn-ghost admin-btn-sm"
                      style={{ padding: '8px 12px' }}
                    >
                      <ExternalLink size={13} /> Open
                    </a>
                  )}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: 600, marginBottom: 4 }}>
                  Artist Official YouTube Channel Link
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="text"
                    readOnly
                    value={selectedReq.youtube_channel_url || ''}
                    className="admin-form-input"
                    style={{ fontSize: 12, color: '#ef4444' }}
                  />
                  {selectedReq.youtube_channel_url && (
                    <a
                      href={selectedReq.youtube_channel_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="admin-btn admin-btn-ghost admin-btn-sm"
                      style={{ padding: '8px 12px', color: '#ef4444' }}
                    >
                      <ExternalLink size={13} /> Open
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* SenritsuX Internal Screening Protocol */}
            <div
              style={{
                background: 'rgba(217, 251, 6, 0.04)',
                border: '1px solid rgba(217, 251, 6, 0.15)',
                borderRadius: 8,
                padding: '12px 14px',
                fontSize: 12,
                color: 'rgba(255,255,255,0.8)',
              }}
            >
              <div style={{ fontWeight: 700, color: '#d9fb06', marginBottom: 6 }}>
                SenritsuX Screening Verification Checklist
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6, color: 'rgba(255,255,255,0.7)' }}>
                <li>Confirm the Topic URL is an official SenritsuX distributed track.</li>
                <li>
                  <strong style={{ color: '#fff' }}>Screening Rule:</strong> Confirm the YouTube Channel has at least 3 public, non-Shorts music videos.
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', display: 'block' }}>
                    (Internal SenritsuX quality screening requirement prior to distributor delivery.)
                  </span>
                </li>
                <li>Confirm channel name closely matches artist stage name with standard branding.</li>
                <li>Confirm no copyright strikes or third-party impersonation.</li>
              </ul>
            </div>

            {/* Admin Review Notes */}
            <div className="admin-form-group" style={{ marginBottom: 0 }}>
              <label className="admin-form-label">Internal Admin Review Notes</label>
              <textarea
                className="admin-form-input admin-form-textarea"
                style={{ minHeight: 65, fontSize: 13 }}
                placeholder="Optional internal notes or inspection logs..."
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
              />
            </div>

            {/* Rejection Reason (always available, required if rejecting) */}
            <div className="admin-form-group" style={{ marginBottom: 0 }}>
              <label className="admin-form-label" style={{ color: '#f87171' }}>
                Rejection Reason (Visible to artist if rejected)
              </label>
              <textarea
                className="admin-form-input admin-form-textarea"
                style={{ minHeight: 65, fontSize: 13, borderColor: rejectionReason ? 'rgba(239,68,68,0.5)' : undefined }}
                placeholder="Required when rejecting (e.g. Channel only has 1 non-Shorts video; Topic link belongs to another distributor; etc.)..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>

            {/* Status Actions */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px solid rgba(255,255,255,0.1)',
                paddingTop: 14,
                marginTop: 4,
              }}
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {selectedReq.status !== 'under_review' && selectedReq.status !== 'approved' && (
                  <button
                    className="admin-btn admin-btn-sm admin-btn-ghost"
                    disabled={savingStatus}
                    onClick={() => handleUpdateStatus('under_review')}
                    style={{ color: '#38bdf8' }}
                  >
                    <RefreshCw size={12} /> Mark Under Review
                  </button>
                )}

                {selectedReq.status !== 'approved' && (
                  <button
                    className="admin-btn admin-btn-sm admin-btn-success"
                    disabled={savingStatus}
                    onClick={() => handleUpdateStatus('approved')}
                  >
                    <CheckCircle size={12} /> Approve OAC
                  </button>
                )}

                {selectedReq.status !== 'rejected' && (
                  <button
                    className="admin-btn admin-btn-sm admin-btn-danger"
                    disabled={savingStatus}
                    onClick={() => handleUpdateStatus('rejected')}
                  >
                    <XCircle size={12} /> Reject
                  </button>
                )}
              </div>

              <button
                className="admin-btn admin-btn-ghost admin-btn-sm"
                onClick={() => setSelectedReq(null)}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ROOT: AdminPage shell
// ══════════════════════════════════════════════════════════════
export default function AdminPage() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [avatarErr, setAvatarErr] = useState(false);
  const [unreadContactCount, setUnreadContactCount] = useState(0);
  const [pendingOacCount, setPendingOacCount] = useState(0);

  // Auto-select tab if navigating directly to /admin/contact-messages or /admin/oac
  useEffect(() => {
    if (location.pathname.includes('contact-messages') || location.pathname.includes('messages')) {
      setActiveTab('messages');
    } else if (location.pathname.includes('oac')) {
      setActiveTab('oac');
    }
  }, [location.pathname]);

  // Periodic/event fetch unread contact messages and pending OAC count for sidebar badges
  useEffect(() => {
    let mounted = true;
    const fetchCounts = () => {
      fetchUnreadContactCount().then(({ count }) => {
        if (mounted) setUnreadContactCount(count || 0);
      });
      fetchPendingOacCount().then(({ count }) => {
        if (mounted) setPendingOacCount(count || 0);
      });
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 20000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [activeTab]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const TAB_TITLES = {
    overview:      'Overview',
    artists:       'Artists & Profiles',
    submissions:   'A&R Submissions',
    oac:           'YouTube Official Artist Channel (OAC) Requests',
    storage:       'Storage Index',
    messages:      'Contact Messages',
    financials:    'Financials',
    notifications: 'Notifications',
    audit:         'Audit Log',
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':      return <AdminOverview />;
      case 'artists':       return <AdminArtists adminId={user?.id} />;
      case 'submissions':   return <AdminSubmissions adminId={user?.id} />;
      case 'oac':           return <AdminOacRequests adminId={user?.id} />;
      case 'storage':       return <AdminStorageIndex adminId={user?.id} />;
      case 'messages':      return <AdminContactMessages adminId={user?.id} />;
      case 'financials':    return <AdminFinancials adminId={user?.id} />;
      case 'notifications': return <AdminNotifications adminId={user?.id} />;
      case 'audit':         return <AdminAuditLog />;
      default:              return <AdminOverview />;
    }
  };

  const initials = ((profile?.stage_name || profile?.name || user?.email || 'A')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2));

  return (
    <div className="admin-shell">
      {/* ── Sidebar ── */}
      <aside className="admin-sidebar" role="navigation" aria-label="Admin navigation">
        <div className="admin-sidebar-logo">
          <img src={logo} alt="SenritsuX" />
          <span className="admin-sidebar-badge">Admin</span>
        </div>

        <nav className="admin-nav">
          {TABS.map((tab) => {
            const isMessages = tab.id === 'messages';
            const isOac = tab.id === 'oac';
            const badgeCount = isMessages ? unreadContactCount : isOac ? pendingOacCount : null;

            return (
              <button
                key={tab.id}
                id={`admin-tab-${tab.id}`}
                className={`admin-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                aria-current={activeTab === tab.id ? 'page' : undefined}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {tab.icon}
                  {tab.label}
                </span>
                {badgeCount > 0 && (
                  <span style={{
                    background: isOac ? '#ef4444' : '#d9fb06',
                    color: isOac ? '#fff' : '#000',
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '2px 7px',
                    borderRadius: '10px',
                    lineHeight: 1,
                  }}>
                    {badgeCount}
                  </span>
                )}
              </button>
            );
          })}

          <div className="admin-nav-divider" />

          <button
            className="admin-nav-item"
            onClick={() => navigate('/dashboard')}
          >
            <LayoutDashboard size={17} />
            Artist Dashboard
          </button>
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-sidebar-user">
            <div className="admin-sidebar-avatar">
              {profile?.avatar_url && !avatarErr ? (
                <img
                  src={profile.avatar_url}
                  alt={initials}
                  onError={() => setAvatarErr(true)}
                />
              ) : (
                initials
              )}
            </div>
            <div className="admin-sidebar-user-info">
              <div className="admin-sidebar-user-name">
                {profile?.stage_name || profile?.name || user?.email?.split('@')[0] || 'Admin'}
              </div>
              <div className="admin-sidebar-user-role">Admin</div>
            </div>
          </div>

          <button
            id="admin-signout-btn"
            className="admin-nav-item"
            onClick={handleSignOut}
            style={{ color: 'rgba(239,68,68,0.7)' }}
          >
            <LogOut size={17} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="admin-main">
        <header className="admin-topbar">
          <div>
            <div className="admin-topbar-title">{TAB_TITLES[activeTab]}</div>
            <div className="admin-topbar-subtitle">SenritsuX Admin Panel</div>
          </div>
        </header>

        <main className="admin-content" id="admin-content" role="main">
          {renderTab()}
        </main>
      </div>
    </div>
  );
}
